/**
 * Resultado por Plano — DRE mensal por plano a partir da contabilidade do MV (ADR 0014).
 *
 * Le SO a copia local (GET /resultado-plano/*). Abas: Painel (linhas x meses, drill ate
 * a conta), Por plano (comparativo), Regras (linhas/planos, editaveis pelo gestor, com
 * historico) e Cargas (sync e reconciliacao).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PieChart, RefreshCw, ChevronRight, ChevronDown, X, AlertTriangle, Settings, Layers, Database, TrendingUp, LayoutGrid, BarChart3, Activity } from "lucide-react";
import { ComposedChart, LineChart, Line, Bar, Cell, XAxis, YAxis, Tooltip as RTooltip, Legend, CartesianGrid, ResponsiveContainer } from "recharts";
import api from "../services/api";
import InfoTip from "../components/InfoTip";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const MESES = ["", "Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const T = {
  bg: "#f5f7fa", card: "#ffffff", ink: "#2c3e50", inkSoft: "#7f8c8d", line: "#e8e8e8",
  teal: "var(--brand, #20a490)", tealSoft: "#e8f6f4", risco: "#e74c3c", riscoSoft: "#fdecea",
  ambar: "#f39c12", ambarSoft: "#fef5e7", azul: "#3498db", verde: "#27ae60",
};
const MONO = "'SF Mono','JetBrains Mono',Consolas,monospace";

// Tooltip ⓘ: o compartilhado (portal — não é cortado por tabela com rolagem).

const fmt = (v) => Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Mês corrente (YYYY-MM): ainda em andamento — sem balancete fechado.
const mesCorrente = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const mesLabel = (m) => { const [a, me] = String(m).split("-"); return `${MESES[Number(me)]}/${a.slice(2)}`; };
const dataHora = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "—");
const dataBR = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

/* Tendências — o que mudou. A matriz 27 planos × 12 meses é cega para
   movimento: o olho não acha o outlier sozinho. Nada aqui diz que algo está
   errado, então nada é vermelho. */
function Tendencias({ dados: t }) {
  if (!t) return null;
  const { variacoes = [], cruzamentos = [], concentracao } = t;
  const conc = concentracao || { planos: [], top3_pct: 0, qt_planos: 0 };
  if (!variacoes.length && !cruzamentos.length && conc.qt_planos < 2) return null;

  return (
    <div style={{ border: `1px solid ${T.line}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, background: T.card }}>
      {variacoes.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>
            Variação fora do padrão ({variacoes.length})
          </div>
          <div style={{ fontSize: 11, color: T.inkSoft, marginBottom: 3 }}>
            Comparada com a oscilação habitual de cada linha, não com um percentual fixo.
            As células estão marcadas na tabela abaixo.
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {variacoes.slice(0, 8).map((v, i) => (
              <li key={i} style={{ fontSize: 12 }}>
                <b>{v.rotulo}</b> · {mesLabel(v.mes)} ·{" "}
                <span style={{ fontFamily: MONO }}>{fmt(v.de)} → {fmt(v.para)}</span>{" "}
                ({v.delta > 0 ? "+" : ""}{fmt(v.delta)})
              </li>
            ))}
            {variacoes.length > 8 && <li style={{ fontSize: 12, color: T.inkSoft }}>… e mais {variacoes.length - 8}</li>}
          </ul>
        </div>
      )}

      {cruzamentos.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>
            Plano que cruzou o zero ({cruzamentos.length})
          </div>
          <ul style={{ margin: "3px 0 0", paddingLeft: 18 }}>
            {cruzamentos.map((c, i) => (
              <li key={i} style={{ fontSize: 12 }}>
                <b>{c.cd_plano} · {c.ds_plano}</b> · {mesLabel(c.mes)} ·{" "}
                {c.sentido === "virou_deficit"
                  ? <span style={{ color: T.risco }}>entrou no vermelho</span>
                  : <span style={{ color: T.verde }}>saiu do vermelho</span>}{" "}
                <span style={{ fontFamily: MONO, color: T.inkSoft }}>({fmt(c.de)} → {fmt(c.para)})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {conc.qt_planos >= 2 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>Concentração</div>
          <div style={{ fontSize: 12, color: T.inkSoft }}>
            Os 3 maiores respondem por <b style={{ color: T.ink }}>{conc.top3_pct}%</b> do resultado,
            entre {conc.qt_planos} planos.{" "}
            {conc.planos.slice(0, 3).map((p) => `${p.cd_plano} (${p.pct}%)`).join(" · ")}
          </div>
          <div style={{ fontSize: 11, color: T.inkSoft, marginTop: 2 }}>
            Em módulo: somar resultado positivo com negativo faria um plano no vermelho
            cancelar outro no azul, e a conta diria que nenhum dos dois pesa.
          </div>
        </div>
      )}
    </div>
  );
}

/* Anomalias — "o dinheiro está aqui, mas chegou do jeito certo?".
   Fica FORA da faixa de conferência e nasce recolhida: nenhum item aqui
   invalida o painel, e misturar revisão com defeito ensina a ignorar os dois. */
function Anomalias({ ano }) {
  const [a, setA] = useState(null);
  const [aberto, setAberto] = useState(false);
  useEffect(() => {
    api.get("/resultado-plano/anomalias", { params: { ano } })
      .then((r) => setA(r.data)).catch(() => setA(null));
  }, [ano]);
  if (!a || !a.qt_total) return null;

  const Bloco = ({ titulo, explica, itens, render }) => (
    itens.length === 0 ? null : (
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>{titulo} ({itens.length})</div>
        <div style={{ fontSize: 11, color: T.inkSoft, marginBottom: 3 }}>{explica}</div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {itens.slice(0, 15).map((x, i) => <li key={i} style={{ fontSize: 12 }}>{render(x)}</li>)}
          {itens.length > 15 && <li style={{ fontSize: 12, color: T.inkSoft }}>… e mais {itens.length - 15}</li>}
        </ul>
      </div>
    )
  );

  return (
    <div style={{ border: `1px solid ${T.line}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, background: T.card }}>
      <div onClick={() => setAberto(!aberto)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: T.ink }}>
        {aberto ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        <b>{a.qt_total}</b> ponto(s) para revisar. O painel fecha, isto não impede a leitura
      </div>

      {aberto && (<>
        <Bloco titulo="Saldo de apuração fora de dezembro"
          explica="Esse saldo prevalece sobre o mensal. Fora de dezembro, entra onde não devia."
          itens={a.sem_apuracao_fora_de_dezembro}
          render={(x) => <>{mesLabel(x.mes)} · {x.qt} lançamento(s) · <span style={{ fontFamily: MONO }}>{fmt(x.vl)}</span></>} />

        <Bloco titulo="Dezembro sem saldo de apuração"
          explica="A operadora usa o mecanismo em outros meses, mas não neste dezembro."
          itens={a.dezembro_sem_apuracao} render={(m) => mesLabel(m)} />

        <Bloco titulo="Contas contábeis novas"
          explica="Apareceram pela primeira vez. As marcadas entraram numa linha por regra de prefixo; ninguém decidiu incluí-las."
          itens={a.contas_novas}
          render={(x) => <>
            <span style={{ fontFamily: MONO }}>{x.cd_contabil}</span> · {x.ds_conta} · {mesLabel(x.mes)} ·{" "}
            <span style={{ fontFamily: MONO }}>{fmt(x.vl)}</span>
            {x.linha ? (x.por_prefixo
              ? <b style={{ color: T.ambar }}> → entrou em {x.linha} por prefixo</b>
              : <> → {x.linha}</>)
              : <b style={{ color: T.ambar }}> → fora de todas as linhas</b>}
          </>} />

        <Bloco titulo="Sinal invertido o ano todo"
          explica="Negativa em todos os meses em que aparece. Conta redutora é assim mesmo, confira se é o caso."
          itens={a.sinal_invertido}
          render={(x) => <>
            <span style={{ fontFamily: MONO }}>{x.cd_contabil}</span> · {x.ds_conta} ·{" "}
            {x.meses} mes(es) · <span style={{ fontFamily: MONO }}>{fmt(x.vl_total)}</span>
          </>} />
      </>)}
    </div>
  );
}

/* Faixa de conferência — responde "o painel está escondendo dinheiro?".
   Um DRE errado por OMISSÃO parece perfeito: não há célula vermelha, o número
   só está menor do que deveria. Os três baldes de fora são as três formas de o
   valor sumir, e cada um tem providência diferente — por isso não viram um
   número só. */
function Conferencia({ ano, incluirSemPlano, versao, onVerNaoClassificadas }) {
  const [c, setC] = useState(null);
  useEffect(() => {
    api.get("/resultado-plano/conferencia", { params: { ano, incluir_sem_plano: incluirSemPlano } })
      .then((r) => setC(r.data)).catch(() => setC(null));
  }, [ano, incluirSemPlano, versao]);
  if (!c || !c.meses.length) return null;

  const a = c.ano_todo;
  const cob = c.cobertura || { buracos: [], atraso: [], completo: true };
  const temFora = Math.abs(a.fora_do_painel) >= 0.01;
  const naoFecha = !c.fecha;
  const temBuraco = cob.buracos.length > 0;
  const temRevisao = (c.revisoes || []).length > 0;
  // Buraco é defeito; atraso pode ser a contabilidade que não fechou; revisão
  // é fato normal da contabilidade que só precisa ser dito. Cores diferentes,
  // senão quem opera aprende a ignorar as três.
  const grave = naoFecha || temBuraco;
  const atencao = temFora || cob.atraso.length > 0 || temRevisao;

  const Balde = ({ rotulo, valor, dica, alerta, onClick }) => (
    <div onClick={onClick}
      style={{ flex: "1 1 150px", minWidth: 140, cursor: onClick ? "pointer" : "default" }}>
      <div style={{ fontSize: 11, color: T.inkSoft, marginBottom: 2 }}>{rotulo}{dica && <InfoTip texto={dica} />}</div>
      <div style={{ fontFamily: MONO, fontSize: 15, fontWeight: 700,
                    color: alerta && Math.abs(valor) >= 0.01 ? T.ambar : T.ink }}>{fmt(valor)}</div>
    </div>
  );

  return (
    <div style={{
      background: grave ? T.riscoSoft : (atencao ? T.ambarSoft : T.card),
      border: `1px solid ${grave ? T.risco : (atencao ? T.ambar : T.line)}`,
      borderRadius: 10, padding: "12px 16px", marginBottom: 14,
    }}>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Balde rotulo="No painel" valor={a.no_painel_classificado}
               dica="Valor que aparece em alguma linha do DRE" />
        <Balde rotulo="Fora das regras" valor={a.no_painel_nao_classificado} alerta
               dica="Conta com movimento que não casa com nenhuma linha: falta criar a regra"
               onClick={onVerNaoClassificadas} />
        <Balde rotulo="Setor sem plano" valor={a.sem_plano} alerta
               dica="Administrativo, não rateado. Marque 'incluir administrativo' para somar" />
        <Balde rotulo="Plano não liberado" valor={a.plano_fora_do_painel} alerta
               dica="Plano descoberto no sync, aguardando decisão na aba Regras e planos" />
        <div style={{ borderLeft: `1px solid ${T.line}`, paddingLeft: 20, flex: "1 1 170px", minWidth: 160 }}>
          <div style={{ fontSize: 11, color: T.inkSoft, marginBottom: 2 }}>Total da contabilidade</div>
          <div style={{ fontFamily: MONO, fontSize: 15, fontWeight: 800 }}>{fmt(a.total_fonte)}</div>
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: naoFecha ? T.risco : T.inkSoft }}>
        {naoFecha ? (
          <><AlertTriangle size={13} /> <b>A conferência não fecha:</b> sobra {fmt(a.diferenca)} entre o
            total da contabilidade e a soma dos baldes. É defeito de cálculo do painel, não da
            contabilidade. Não use estes números até resolver.</>
        ) : temFora ? (
          <><AlertTriangle size={13} /> <b>{a.pct_fora}%</b> do movimento de {ano} está fora das colunas
            do painel ({fmt(a.fora_do_painel)}). A conta fecha, então nada se perdeu, mas esse valor
            não está sendo lido por ninguém.</>
        ) : cob.completo ? (
          <>Conferido: todo o movimento de {ano} aparece no painel.</>
        ) : (
          // Sem esta ressalva a faixa diria "está tudo aqui" sobre um ano ao
          // qual falta mês — verdade sobre o que foi carregado, mentira sobre o ano.
          <>Conferido: todo o movimento <b>já carregado</b> aparece no painel.</>
        )}
      </div>

      {/* Mês sem carga não vira coluna: a tabela fecha o buraco sozinha e o
          "Total do ano" soma onze meses sem avisar. Aqui é o único lugar em que
          a ausência fica visível. */}
      {temBuraco && (
        <div style={{ marginTop: 8, fontSize: 12, color: T.risco }}>
          <AlertTriangle size={13} /> <b>Faltam meses no meio da série:</b>{" "}
          {cob.buracos.map(mesLabel).join(", ")}. A carga desses meses não chegou, e eles
          não aparecem como coluna: o total do ano está incompleto.
        </div>
      )}
      {!temBuraco && cob.atraso.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: T.inkSoft }}>
          Ainda sem dado: {cob.atraso.map(mesLabel).join(", ")}. São meses já fechados que não
          chegaram. Pode ser a contabilidade que não fechou, ou o sync parado.
        </div>
      )}

      {/* Mês fechado que mudou de valor depois de lido. Não é erro do painel:
          a contabilidade reprocessa. O problema é o silêncio — o número que
          alguém levou para uma reunião deixa de existir sem avisar. */}
      {(c.revisoes || []).length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: T.ink }}>
          <AlertTriangle size={13} color={T.ambar} /> <b>Mês fechado revisado pela
          contabilidade:</b>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {c.revisoes.map((r) => (
              <li key={r.mes} style={{ fontSize: 12 }}>
                <b>{mesLabel(r.mes)}</b> passou de <span style={{ fontFamily: MONO }}>{fmt(r.vl_antes)}</span> para{" "}
                <span style={{ fontFamily: MONO }}>{fmt(r.vl_depois)}</span>{" "}
                ({r.delta > 0 ? "+" : ""}{fmt(r.delta)}{r.pct != null && ` · ${r.pct}%`})
                <span style={{ color: T.inkSoft }}> · detectado em {dataHora(r.detectado_em)}</span>
              </li>
            ))}
          </ul>
          <div style={{ color: T.inkSoft, marginTop: 3 }}>
            Relatório tirado antes dessa data mostra outro número.
          </div>
        </div>
      )}
    </div>
  );
}

const Card = ({ children, style }) => (
  <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 12, padding: 20, ...style }}>{children}</div>
);
const Botao = ({ children, onClick, primario, disabled, pequeno }) => (
  <button onClick={onClick} disabled={disabled} style={{
    display: "inline-flex", alignItems: "center", gap: 6, padding: pequeno ? "5px 10px" : "8px 14px",
    borderRadius: 8, fontSize: pequeno ? 12 : 13, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
    border: primario ? "none" : `1px solid ${T.line}`, background: primario ? T.teal : "#fff",
    color: primario ? "#fff" : T.ink, opacity: disabled ? 0.6 : 1 }}>{children}</button>
);
const Aba = ({ ativa, onClick, icone: Icone, children }) => (
  <button onClick={onClick} style={{
    display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "none", cursor: "pointer",
    borderBottom: `2px solid ${ativa ? T.teal : "transparent"}`, background: "transparent",
    color: ativa ? T.teal : T.inkSoft, fontWeight: 600, fontSize: 13 }}>
    <Icone size={15} />{children}
  </button>
);
const TH = { padding: "8px 8px", fontWeight: 600, position: "sticky", top: 0, background: "#fff", fontSize: 12, color: T.inkSoft, borderBottom: `1px solid ${T.line}` };
const TD = { padding: "6px 8px", color: T.ink, fontSize: 13, borderBottom: `1px solid ${T.line}` };
const NUM = { ...TD, fontFamily: MONO, textAlign: "right", whiteSpace: "nowrap" };

const Valor = ({ v, negrito }) => (
  <span style={{ color: v < 0 ? T.risco : T.ink, fontWeight: negrito ? 700 : 400 }}>{v === 0 ? "—" : fmt(v)}</span>
);

/* ---------- Painel: linhas x meses ---------- */
// Celula de variacao %: verde sobe, vermelho desce, "—" quando nao ha base.
const PctCell = ({ v }) => {
  if (v === null || v === undefined) return <span style={{ color: T.inkSoft }}>—</span>;
  const cor = v > 0.05 ? T.verde : v < -0.05 ? T.risco : T.inkSoft;
  const s = `${v > 0 ? "+" : ""}${v.toFixed(1).replace(".", ",")}%`;
  return <span style={{ color: cor }}>{s}</span>;
};

/* ---------- Analitico: linhas x meses com variacao % mes a mes (analise horizontal) ---------- */
function Analitico({ dados }) {
  const [fechadas, setFechadas] = useState(() => new Set());
  const filhos = useMemo(() => {
    const m = {};
    (dados?.linhas || []).forEach((l) => { if (l.pai) (m[l.pai] = m[l.pai] || []).push(l.codigo); });
    return m;
  }, [dados]);
  const oculta = (l) => { let p = l.pai; while (p) { if (fechadas.has(p)) return true; p = (dados?.linhas || []).find((x) => x.codigo === p)?.pai; } return false; };
  const alternar = (c) => setFechadas((s) => { const n = new Set(s); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const grupo = (l) => !!filhos[l.codigo];
  if (!dados || !dados.meses.length) return <Card><p style={{ color: T.inkSoft }}>Sem dados para o analítico em {dados ? dados.ano : ""}.</p></Card>;
  const meses = dados.meses;
  const mesBase = dados.mes_base || null;   // dezembro do ano anterior, se houver
  // variacao % de `item` no mes i: contra o mes i-1; no primeiro mes, contra a
  // base (dez do ano anterior). Base zero/ausente -> null ("—").
  const deltaPct = (item, i) => {
    const ant = i === 0 ? Number(item.base ?? 0) : Number(item.valores[meses[i - 1]] || 0);
    if (Math.abs(ant) < 0.005) return null;
    return (Number(item.valores[meses[i]] || 0) - ant) / Math.abs(ant) * 100;
  };
  // Coluna de rotulo fixa (nao some ao rolar pro lado) e header fixo (nao some ao
  // rolar pra baixo). Fundo opaco por celula: sticky transparente deixa o conteudo
  // rolado aparecer por baixo.
  const stickyLeft = (bg, z = 1) => ({ position: "sticky", left: 0, background: bg, zIndex: z });
  return (
    <Card style={{ padding: 8 }}>
      <p style={{ fontSize: 12, color: T.inkSoft, padding: "4px 8px", margin: 0 }}>
        Análise horizontal: cada mês vem seguido da variação <b>Δ%</b> em relação ao mês anterior.
        {mesBase ? <> O primeiro mês compara com <b>{mesLabel(mesBase)}</b> (dezembro do ano anterior).</> : <> O primeiro mês não tem base do ano anterior, então aparece como "—".</>} Base zero aparece como "—".
      </p>
      <div style={{ overflow: "auto", maxHeight: "68vh", border: `1px solid ${T.line}`, borderRadius: 8 }}>
        <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%", minWidth: 720 }}>
          <thead>
            <tr>
              <th style={{ ...TH, textAlign: "left", minWidth: 300, ...stickyLeft("#fff", 3) }}>Linha<InfoTip w={280} texto="Valores já com o sinal da natureza (receita = crédito − débito; despesa = débito − crédito). Δ% = (mês − mês anterior) / |mês anterior|; no primeiro mês, contra dezembro do ano anterior." /></th>
              {meses.map((m, i) => (
                <React.Fragment key={m}>
                  <th style={{ ...TH, textAlign: "right", zIndex: 2 }}>{mesLabel(m)}</th>
                  <th style={{ ...TH, textAlign: "right", color: T.inkSoft, zIndex: 2 }}>Δ%</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {dados.linhas.filter((l) => !oculta(l)).map((l) => {
              const bg = l.nivel === 0 ? "#fafbfc" : "#fff";
              return (
              <tr key={l.codigo} style={{ background: bg }}>
                <td style={{ ...TD, paddingLeft: 8 + l.nivel * 18, fontWeight: l.nivel <= 1 ? 700 : 400, whiteSpace: "nowrap", ...stickyLeft(bg, 1) }}>
                  {grupo(l) ? (
                    <span onClick={() => alternar(l.codigo)} style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {fechadas.has(l.codigo) ? <ChevronRight size={14} /> : <ChevronDown size={14} />}{l.rotulo}
                    </span>
                  ) : <span style={{ marginLeft: 18 }}>{l.rotulo}</span>}
                </td>
                {meses.map((m, i) => (
                  <React.Fragment key={m}>
                    <td style={NUM}><Valor v={l.valores[m]} negrito={l.nivel <= 1} /></td>
                    <td style={{ ...NUM, fontWeight: 400 }}><PctCell v={deltaPct(l, i)} /></td>
                  </React.Fragment>
                ))}
              </tr>
            );})}
            {dados.totais.map((t) => (
              <tr key={t.codigo} style={{ background: "#eef3f8" }}>
                <td style={{ ...TD, fontWeight: 800, ...stickyLeft("#eef3f8", 1) }}>{t.rotulo}</td>
                {meses.map((m, i) => (
                  <React.Fragment key={m}>
                    <td style={NUM}><Valor v={t.valores[m]} negrito /></td>
                    <td style={NUM}><PctCell v={deltaPct(t, i)} /></td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function TabelaPainel({ dados, onCelula, destaques }) {
  const filhos = useMemo(() => {
    const m = {};
    dados.linhas.forEach((l) => { if (l.pai) (m[l.pai] = m[l.pai] || []).push(l.codigo); });
    return m;
  }, [dados]);
  // Abre só os dois primeiros níveis (Receitas > Receita Operacional); do 2º
  // nível para baixo começa fechado e o usuário abre o que quiser investigar.
  const gruposProfundos = useMemo(() => dados.linhas.filter((l) => filhos[l.codigo] && l.nivel >= 1).map((l) => l.codigo), [dados, filhos]);
  const chaveArvore = gruposProfundos.join("|");
  const [fechadas, setFechadas] = useState(() => new Set(gruposProfundos));
  useEffect(() => { setFechadas(new Set(gruposProfundos)); }, [chaveArvore]);
  const oculta = (l) => { let p = l.pai; while (p) { if (fechadas.has(p)) return true; p = dados.linhas.find((x) => x.codigo === p)?.pai; } return false; };
  const alternar = (c) => setFechadas((s) => { const n = new Set(s); n.has(c) ? n.delete(c) : n.add(c); return n; });
  // Abertura por grupo de conta do razão: começa fechada, abre na seta da linha.
  const [comContas, setComContas] = useState(() => new Set());
  const alternarContas = (c) => setComContas((s) => { const n = new Set(s); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const colunas = dados.meses;
  const grupo = (l) => !!filhos[l.codigo];
  const abre = (l) => !grupo(l) && (l.contas || []).length > 0;
  const btnArvore = { fontSize: 11, padding: "3px 8px", borderRadius: 6, border: `1px solid ${T.line}`, background: "#fff", color: T.inkSoft, cursor: "pointer" };
  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
        <button style={btnArvore} onClick={() => setFechadas(new Set())}>Expandir tudo</button>
        <button style={btnArvore} onClick={() => setFechadas(new Set(gruposProfundos))}>Recolher</button>
      </div>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 720 }}>
        <thead>
          <tr>
            <th style={{ ...TH, textAlign: "left", minWidth: 300 }}>Linha<InfoTip w={300} texto="Cada linha agrupa contas por prefixo do CD_CONTABIL. Os valores são o líquido (Movimento) com o sinal da natureza: receita = crédito − débito; despesa = débito − crédito. A seta de uma linha abre os grupos de conta do razão que ela soma, mês a mês: é onde aparece valor lançado no grupo errado. Clique num valor para ver as contas e os setores." /></th>
            {colunas.map((m) => <th key={m} style={{ ...TH, textAlign: "right" }}>{mesLabel(m)}</th>)}
            <th style={{ ...TH, textAlign: "right", background: T.tealSoft }}>Total do ano<InfoTip texto="Soma os meses presentes na cópia local. Mês sem dado não entra; confira buracos na Conferência acima." /></th>
          </tr>
        </thead>
        <tbody>
          {dados.linhas.filter((l) => !oculta(l)).map((l) => (
            <React.Fragment key={l.codigo}>
            <tr style={{ background: l.nivel === 0 ? "#fafbfc" : "transparent" }}>
              <td style={{ ...TD, paddingLeft: 8 + l.nivel * 18, fontWeight: l.nivel <= 1 ? 700 : 400, whiteSpace: "nowrap" }}>
                {grupo(l) ? (
                  <span onClick={() => alternar(l.codigo)} style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {fechadas.has(l.codigo) ? <ChevronRight size={14} /> : <ChevronDown size={14} />}{l.rotulo}
                  </span>
                ) : abre(l) ? (
                  <span onClick={() => alternarContas(l.codigo)} title="Ver os grupos de conta desta linha"
                    style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {comContas.has(l.codigo) ? <ChevronDown size={14} color={T.inkSoft} /> : <ChevronRight size={14} color={T.inkSoft} />}{l.rotulo}
                    <span style={{ color: T.inkSoft, fontSize: 11 }}>({l.contas.length})</span>
                  </span>
                ) : <span style={{ marginLeft: 18 }}>{l.rotulo}</span>}
              </td>
              {colunas.map((m) => {
                const d = destaques && destaques.get(`${l.codigo}|${m}`);
                return (
                  <td key={m} title={d
                      ? `Variação fora do padrão desta linha: ${fmt(d.de)} → ${fmt(d.para)} (${d.delta > 0 ? "+" : ""}${fmt(d.delta)})`
                      : "Ver as contas desta linha no mês"}
                    style={{ ...NUM, cursor: "pointer",
                             background: d ? T.ambarSoft : undefined,
                             boxShadow: d ? `inset 3px 0 0 ${T.ambar}` : undefined }}
                    onClick={() => onCelula(l, m)}>
                    <Valor v={l.valores[m]} negrito={l.nivel <= 1} />
                  </td>
                );
              })}
              <td style={{ ...NUM, background: T.tealSoft }}><Valor v={l.total} negrito /></td>
            </tr>
            {abre(l) && comContas.has(l.codigo) && l.contas.map((c) => (
              <tr key={`${l.codigo}|${c.conta}`} style={{ background: "#fbfcfd" }}>
                <td style={{ ...TD, paddingLeft: 8 + (l.nivel + 1) * 18 + 18, fontSize: 12, color: T.inkSoft, whiteSpace: "nowrap" }}
                    title={c.descricao}>
                  <span style={{ fontFamily: MONO, fontSize: 11 }}>{c.conta}</span>{" "}
                  <span>{c.descricao.length > 44 ? `${c.descricao.slice(0, 44)}…` : c.descricao}</span>
                </td>
                {colunas.map((m) => (
                  <td key={m} title="Ver as contas desta linha no mês" style={{ ...NUM, fontSize: 12, color: T.inkSoft, cursor: "pointer" }}
                      onClick={() => onCelula(l, m)}>
                    <Valor v={c.valores[m]} />
                  </td>
                ))}
                <td style={{ ...NUM, fontSize: 12, color: T.inkSoft, background: T.tealSoft }}><Valor v={c.total} /></td>
              </tr>
            ))}
            </React.Fragment>
          ))}
          {dados.totais.map((t) => (
            <tr key={t.codigo} style={{ background: "#eef3f8" }}>
              <td style={{ ...TD, fontWeight: 800 }} title={t.formula}>{t.rotulo} <span style={{ color: T.inkSoft, fontWeight: 400, fontSize: 11 }}>({t.formula})</span></td>
              {colunas.map((m) => {
                const d = destaques && destaques.get(`${t.codigo}|${m}`);
                return <td key={m} title={d ? `Variação fora do padrão: ${fmt(d.de)} → ${fmt(d.para)}` : undefined}
                  style={{ ...NUM, background: d ? T.ambarSoft : undefined,
                           boxShadow: d ? `inset 3px 0 0 ${T.ambar}` : undefined }}>
                  <Valor v={t.valores[m]} negrito /></td>;
              })}
              <td style={{ ...NUM, background: T.tealSoft }}><Valor v={t.total} negrito /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Drill: contas de uma linha num mes ---------- */
function ModalContas({ alvo, meses, planos, incluirSemPlano, onFechar }) {
  // Mes selecionavel DENTRO do modal: "" = ano inteiro (casa com o valor do painel).
  // Drill de celula/ocorrencia abre no mes clicado; o card "Fora das regras" abre no ano.
  const [mesSel, setMesSel] = useState(alvo.mes ? String(Number(alvo.mes.split("-")[1])) : "");
  const [contas, setContas] = useState(null);
  useEffect(() => {
    setContas(null);
    api.get("/resultado-plano/painel/contas", { params: { ano: alvo.ano, mes: mesSel || undefined, codigo: alvo.codigo, planos: planos || undefined, incluir_sem_plano: incluirSemPlano } })
      .then((r) => setContas(r.data)).catch((e) => { toast.error(e?.response?.data?.detail || "Falha ao carregar contas"); onFechar(); });
  }, [alvo, mesSel, planos, incluirSemPlano, onFechar]);
  const total = (contas || []).reduce((s, c) => s + c.vl_movimento, 0);
  const periodo = mesSel ? mesLabel(`${alvo.ano}-${String(mesSel).padStart(2, "0")}`) : `${alvo.ano} (ano)`;
  return (
    <div onClick={onFechar} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "min(1100px, 100%)", maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: `1px solid ${T.line}` }}>
          <div>
            <div style={{ fontWeight: 700, color: T.ink }}>{alvo.rotulo} · {periodo}</div>
            <div style={{ fontSize: 12, color: T.inkSoft }}>Contas e setores que compõem a linha. Movimento já com o sinal da natureza.</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select value={mesSel} onChange={(e) => setMesSel(e.target.value)} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${T.line}` }}>
              <option value="">Ano inteiro ({alvo.ano})</option>
              {(meses || []).map((m) => <option key={m} value={Number(m.split("-")[1])}>{mesLabel(m)}</option>)}
            </select>
            <button onClick={onFechar} style={{ border: "none", background: "transparent", cursor: "pointer" }}><X size={18} /></button>
          </div>
        </div>
        <div style={{ overflow: "auto", padding: "0 18px 18px" }}>
          {!contas ? <p style={{ color: T.inkSoft }}>Carregando…</p> : contas.length === 0 ? <p style={{ color: T.inkSoft, marginTop: 14 }}>Nenhuma conta com movimento.</p> : (
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead><tr>
                <th style={{ ...TH, textAlign: "left" }}>Conta</th><th style={{ ...TH, textAlign: "left" }}>Descrição</th>
                <th style={{ ...TH, textAlign: "left" }}>Setor / plano<InfoTip texto="O setor entra no plano por CD_CEN_CUS = CD_PLANO. Setor sem plano é administrativo (só aparece com 'incluir administrativo')." /></th><th style={{ ...TH, textAlign: "right" }}>Débito<InfoTip texto="Valor bruto lançado a débito no período. O líquido é o Movimento." /></th>
                <th style={{ ...TH, textAlign: "right" }}>Crédito<InfoTip texto="Valor bruto lançado a crédito no período. O líquido é o Movimento." /></th><th style={{ ...TH, textAlign: "right" }}>Movimento<InfoTip texto="Líquido, já com o sinal da natureza: receita (conta iniciando em 3) = crédito − débito; despesa (conta em 4) = débito − crédito." /></th><th style={TH}>Origem<InfoTip texto="'mensal' = saldo do mês. 'sem_apuracao' = saldo antes da apuração do resultado, que prevalece quando existe (na prática, dezembro)." /></th>
              </tr></thead>
              <tbody>
                {contas.map((c, i) => (
                  <tr key={i}>
                    <td style={{ ...TD, fontFamily: MONO, whiteSpace: "nowrap" }}>{c.cd_contabil}<div style={{ fontSize: 11, color: T.inkSoft }}>red. {c.cd_reduzido}</div></td>
                    <td style={TD}>{c.ds_conta}</td>
                    <td style={TD}>{c.nm_setor}<div style={{ fontSize: 11, color: T.inkSoft }}>{c.cd_plano ? `${c.cd_plano} · ${c.ds_plano}` : "sem plano"}</div></td>
                    <td style={NUM}>{fmt(c.vl_debito)}</td><td style={NUM}>{fmt(c.vl_credito)}</td>
                    <td style={NUM}><Valor v={c.vl_movimento} /></td>
                    <td style={{ ...TD, fontSize: 11, color: T.inkSoft }}>{c.origem}</td>
                  </tr>
                ))}
                <tr><td colSpan={5} style={{ ...TD, fontWeight: 700, textAlign: "right" }}>Total</td><td style={NUM}><Valor v={total} negrito /></td><td style={TD} /></tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* Reconciliação visível (#125/#129): o movimento da linha Eventos do balancete
   contra o movimento da soma dos grupos de procedimento (utilização). */
function Reconciliacao({ balAnt, balAtual, utiAnt, utiAtual, regime, fora }) {
  if (balAnt === undefined || balAtual === undefined) {
    return <p style={{ fontSize: 12, color: T.inkSoft, margin: "0 0 10px" }}>Carregando a linha Eventos do balancete para reconciliar…</p>;
  }
  if (balAnt === null || balAtual === null) {
    return (
      <p style={{ fontSize: 12, color: T.inkSoft, margin: "0 0 10px" }}>
        <AlertTriangle size={12} color={T.ambar} /> Sem balancete fechado para {balAtual === null ? "este mês" : "o mês anterior"}. A reconciliação aparece quando o mês fechar na contabilidade.
      </p>
    );
  }
  const dBal = balAtual - balAnt, dUti = utiAtual - utiAnt, dif = dBal - dUti;
  const pctExpl = dBal ? Math.round((dUti / dBal) * 100) : null;
  const cel = { padding: "4px 8px", textAlign: "right", fontFamily: MONO, fontSize: 12 };
  const rot = { padding: "4px 8px", fontSize: 12, color: T.ink };
  const sinal = (v) => (v >= 0 ? "+" : "") + fmt(v);
  return (
    <div style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: "8px 10px", margin: "0 0 12px", background: "#fafbfc" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <b style={{ fontSize: 13, color: T.ink }}>Reconciliação com o balancete ({regime})</b>
        <InfoTip w={340} texto="Confere se as contas médicas (por grupo de procedimento) explicam o movimento da linha Eventos do balancete no mesmo mês. 'Explicado' = quanto da variação do balancete os grupos abaixo cobrem. A diferença é escopo contábil (lançamentos de Eventos sem conta médica correspondente) e deve ser pequena." />
      </div>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr style={{ color: T.inkSoft, fontSize: 11 }}>
            <th style={{ ...rot, color: T.inkSoft, textAlign: "left", fontWeight: 600 }}></th>
            <th style={{ ...cel, color: T.inkSoft, fontWeight: 600 }}>Mês anterior</th>
            <th style={{ ...cel, color: T.inkSoft, fontWeight: 600 }}>Mês</th>
            <th style={{ ...cel, color: T.inkSoft, fontWeight: 600 }}>Variação</th>
          </tr>
        </thead>
        <tbody>
          <tr><td style={rot}>Balancete: linha Eventos</td><td style={cel}>{fmt(balAnt)}</td><td style={cel}>{fmt(balAtual)}</td><td style={{ ...cel, fontWeight: 700 }}>{sinal(dBal)}</td></tr>
          <tr><td style={rot}>Contas médicas: soma dos grupos{fora && (fora.vl_ant || fora.vl_atual) ? " (sem farmácia pós)" : ""}</td><td style={cel}>{fmt(utiAnt)}</td><td style={cel}>{fmt(utiAtual)}</td><td style={{ ...cel, fontWeight: 700 }}>{sinal(dUti)}</td></tr>
          <tr style={{ borderTop: `1px solid ${T.line}` }}><td style={rot}>Diferença (não explicada)</td><td style={cel}>{fmt(balAnt - utiAnt)}</td><td style={cel}>{fmt(balAtual - utiAtual)}</td><td style={{ ...cel, fontWeight: 700 }}>{sinal(dif)}</td></tr>
          {fora && (fora.vl_ant || fora.vl_atual) ? (
            <tr style={{ color: T.inkSoft }}>
              <td style={{ ...rot, color: T.inkSoft }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {fora.rotulo}
                  <InfoTip w={340} texto="A farmácia do pós-estabelecido não é lançada na linha Eventos: vai para a conta 4.4.1.3 'Outras despesas' (no Painel, em Outras Despesas Operacionais) e é recuperada pela 4.4.1.6 '(−) Outras', porque o pós é custeado pela patrocinadora. Por isso fica fora da comparação acima, mas continua visível aqui e nos grupos." />
                </span>
              </td>
              <td style={cel}>{fmt(fora.vl_ant)}</td><td style={cel}>{fmt(fora.vl_atual)}</td><td style={cel}>{sinal(fora.delta)}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {pctExpl !== null && (
        <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 4 }}>
          Os grupos explicam <b style={{ color: Math.abs(100 - pctExpl) <= 15 ? T.verde : T.ambar }}>{pctExpl}%</b> da variação da linha Eventos.
        </div>
      )}
    </div>
  );
}

/* ---------- Diagnóstico: "o que fez a conta aumentar" por grupo de procedimento (#129) ---------- */
/* ---------- Explodir um grupo: o que está dentro ----------
   Nível 1 (pré-agregado, com variação): por procedimento ou por prestador.
   Nível 2 (direto nas contas do mês): quem cobrou / de quem foi a conta.
   Cada item carrega a chave (procedimento, cd_prestador, matrícula) — é o que um
   card do Kanban vai levar quando "marcar caso" existir. */
const NIVEIS_EXPLOSAO = { procedimento: "Por procedimento", prestador: "Por prestador" };
// Só para grupos com medicamento identificado (tradução nome comercial → ANVISA)
const NIVEIS_MEDICAMENTO = { classe: "Por classe terapêutica", principio: "Por princípio ativo" };
const COLUNA_NIVEL = { procedimento: "Procedimento", prestador: "Prestador", classe: "Classe terapêutica", principio: "Princípio ativo" };
const NAO_IDENTIFICADO = "Não identificado (conta sem o nome do remédio)";
const celStatic = { ...TH, position: "static" };
const TIP_MEDICAMENTOS = "Como o sistema reconhece o remédio: a conta traz o nome comercial (ex.: MOUNJARO, KEYTRUDA) e o AURA o traduz para o princípio ativo (tirzepatida, pembrolizumabe) e a classe terapêutica pela base pública de medicamentos registrados da ANVISA, atualizada a cada 30 dias. Na descrição, o princípio e a classe aparecem depois do '·'. 'Não identificado' é conta sem o nome do remédio: o código genérico (ex.: 90000000 MEDICAMENTOS FARMÁCIA), usado nas compras pelo cartão farmácia (PBM), que não informa qual remédio foi comprado, e itens que não são medicamento registrado (leite, fórmula manipulada).";

function ExplodirGrupo({ mes, mesAnt, tp, grupo }) {
  const [nivel, setNivel] = useState("procedimento");
  const [dados, setDados] = useState(null);
  const [itemAberto, setItemAberto] = useState(null);
  const [temMedicamento, setTemMedicamento] = useState(false);
  // o grupo tem remédio identificado? (liga as aberturas por classe e princípio)
  useEffect(() => {
    let vivo = true;
    setTemMedicamento(false);
    api.get("/resultado-plano/utilizacao/itens", { params: { mes, cd_grupo: grupo.cd_grupo, nivel: "classe", tp: tp || undefined, limite: 5 } })
      .then((r) => { if (vivo) setTemMedicamento((r.data.itens || []).some((i) => i.chave !== "?")); }).catch(() => {});
    return () => { vivo = false; };
  }, [mes, tp, grupo.cd_grupo]);
  useEffect(() => { if (!temMedicamento && NIVEIS_MEDICAMENTO[nivel]) setNivel("procedimento"); }, [temMedicamento, nivel]);
  const ehMedicamento = temMedicamento || /FARMAC|MEDICAMENT/i.test(grupo.ds_grupo || "");
  const niveis = temMedicamento ? { ...NIVEIS_EXPLOSAO, ...NIVEIS_MEDICAMENTO } : NIVEIS_EXPLOSAO;
  const porMedicamento = !!NIVEIS_MEDICAMENTO[nivel];
  const semNomeDoRemedio = ehMedicamento && !temMedicamento;
  useEffect(() => {
    let vivo = true;
    setDados(null); setItemAberto(null);
    api.get("/resultado-plano/utilizacao/itens", { params: { mes, mes_ant: mesAnt || undefined, cd_grupo: grupo.cd_grupo, nivel, tp: tp || undefined, limite: 15 } })
      .then((r) => { if (vivo) setDados(r.data); }).catch(() => { if (vivo) toast.error("Falha ao abrir o grupo"); });
    return () => { vivo = false; };
  }, [mes, mesAnt, tp, grupo.cd_grupo, nivel]);
  const seg = (a) => ({ fontSize: 11, padding: "3px 8px", border: "none", cursor: "pointer", background: a ? T.teal : "#fff", color: a ? "#fff" : T.ink, fontWeight: a ? 700 : 400 });
  const num = { ...TD, textAlign: "right", fontFamily: MONO, fontSize: 12, whiteSpace: "nowrap" };
  return (
    <div style={{ background: "#fafbfc", border: `1px solid ${T.line}`, borderRadius: 8, padding: 10, margin: "4px 0 8px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: T.inkSoft }}>Dentro de <b style={{ color: T.ink }}>{grupo.ds_grupo}</b>:</span>
        <div style={{ display: "inline-flex", border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden" }}>
          {Object.entries(niveis).map(([k, r]) => <button key={k} onClick={() => setNivel(k)} style={seg(nivel === k)}>{r}</button>)}
        </div>
        <InfoTip w={330} texto="Os itens que mais mexeram no grupo entre as duas competências: maiores altas em cima, maiores quedas embaixo. Clique num item para ver quem cobrou e de quem foi a conta. Item sem grupo, com código estranho ou valor fora do normal é candidato a correção no ERP." />
        {ehMedicamento && <span style={{ fontSize: 11, color: T.inkSoft, display: "inline-flex", alignItems: "center" }}>Medicamentos<InfoTip w={380} texto={TIP_MEDICAMENTOS} /></span>}
        {dados && dados.qt_itens > dados.itens.length && <span style={{ fontSize: 11, color: T.inkSoft, marginLeft: "auto" }}>{dados.itens.length} de {dados.qt_itens} itens (os que mais mexeram)</span>}
      </div>
      {semNomeDoRemedio && (
        <div style={{ fontSize: 12, color: T.ink, background: "#fff8e1", border: "1px solid #f3d27a", borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>
          As contas deste grupo não trazem o nome do remédio: vêm no código genérico, como as compras pelo cartão farmácia (PBM).
          O detalhe por remédio vem do relatório do cartão importado em Cargas (abaixo, quando houver). Os remédios identificados
          nas próprias contas estão nos grupos de medicamento com nome (ex.: oncologia e imunobiológicos, tabela TUSS de medicamentos).
        </div>
      )}
      {semNomeDoRemedio && <RemediosPbm mes={mes} mesAnt={mesAnt} />}
      {!dados ? <p style={{ fontSize: 12, color: T.inkSoft, margin: 0 }}>Carregando…</p>
        : !dados.itens.length ? <p style={{ fontSize: 12, color: T.inkSoft, margin: 0 }}>Nada neste grupo para o filtro.</p> : (
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>
            <th style={{ ...celStatic, textAlign: "left" }}>{COLUNA_NIVEL[nivel]}</th>
            {mesAnt && <th style={{ ...celStatic, textAlign: "right" }}>{mesLabel(mesAnt)}</th>}
            <th style={{ ...celStatic, textAlign: "right" }}>{mesLabel(mes)}</th>
            {mesAnt && <th style={{ ...celStatic, textAlign: "right" }}>Variação</th>}
            <th style={{ ...celStatic, textAlign: "right" }} title="Quantidade de contas no mês">Qtd</th>
          </tr></thead>
          <tbody>
            {dados.itens.map((it) => {
              const aberto = itemAberto === it.chave;
              return (
                <React.Fragment key={it.chave}>
                  <tr onClick={() => setItemAberto(aberto ? null : it.chave)} style={{ cursor: "pointer", background: aberto ? T.tealSoft : undefined }}>
                    <td style={{ ...TD, fontSize: 12 }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {aberto ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        {!porMedicamento && <span style={{ fontFamily: MONO, fontSize: 11, color: T.inkSoft }}>{it.chave}</span>}
                        <span style={porMedicamento && it.chave === "?" ? { color: T.inkSoft, fontStyle: "italic" } : undefined}>
                          {porMedicamento ? (it.chave === "?" ? NAO_IDENTIFICADO : it.descricao) : (it.descricao || "—")}
                        </span>
                      </span>
                    </td>
                    {mesAnt && <td style={num}>{fmt(it.vl_ant)}</td>}
                    <td style={num}>{fmt(it.vl_atual)}</td>
                    {mesAnt && <td style={{ ...num, fontWeight: 600, color: it.delta > 0 ? T.risco : it.delta < 0 ? T.verde : T.inkSoft }}>{it.delta > 0 ? "+" : ""}{fmt(it.delta)}</td>}
                    <td style={num}>{it.qt_atual}</td>
                  </tr>
                  {aberto && (
                    <tr><td colSpan={mesAnt ? 5 : 3} style={{ padding: "0 0 8px 18px" }}>
                      <DetalheBruto mes={mes} mesAnt={mesAnt} tp={tp} cdGrupo={grupo.cd_grupo} nivel={nivel} chave={it.chave} />
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const EFEITOS = {
  comecaram: { rot: "Começaram a usar", dica: "Tiveram conta neste mês e nenhuma nos 6 meses anteriores. É uso novo de verdade: tratamento começando, gente nova no plano ou troca de canal." },
  voltaram: { rot: "Voltaram após pausa", dica: "Tiveram conta neste mês, não no anterior, mas já tinham nos 6 meses antes. É o ritmo normal de quem compra a cada 30–40 dias: não é gente nova." },
  gastaram_mais: { rot: "Gastaram mais", dica: "Tiveram conta nos dois meses e o valor subiu." },
  gastaram_menos: { rot: "Gastaram menos", dica: "Tiveram conta nos dois meses e o valor caiu." },
  pausaram: { rot: "Pausaram", dica: "Costumam ter conta (3 ou mais dos últimos 6 meses) e não tiveram neste mês. Em geral voltam no seguinte; se não voltarem, entram no alerta de possível abandono." },
  pontual: { rot: "Uso pontual encerrado", dica: "Tiveram conta no mês anterior, mas não costumam ter: uso ocasional que não se repetiu." },
};
const TIP_COMPOSICAO = "Cada pessoa entra em uma categoria olhando os 6 meses anteriores, não só o mês passado: comparar mês a mês exagera o movimento, porque muita gente compra a cada 30–40 dias e \"some\" num mês para voltar no outro. A soma das categorias é exatamente a variação do item.";
const TIP_ABANDONO = "Pessoas que tinham conta com frequência (3 ou mais dos 6 meses anteriores) e estão há 60 dias ou mais sem nenhuma. Não mexem na variação deste mês (a queda delas já aconteceu), por isso ficam fora da soma. Pode ser falta de adesão ao tratamento, troca de canal (ex.: passou a pedir reembolso) ou saída do plano: vale olhar caso a caso.";

const NIVEIS_PBM = { classe: "Por classe terapêutica", principio: "Por princípio ativo", produto: "Por produto", farmacia: "Por farmácia", medico: "Por médico" };
const TIP_PBM_DIAG = "Vem do relatório detalhado de compras do cartão farmácia, importado em Cargas. O mês é o da VENDA (a conta médica usa o mês de apresentação da cobrança), por isso o total pode diferir um pouco do grupo acima. O remédio é traduzido para princípio ativo e classe pela base pública da ANVISA.";

function RemediosPbm({ mes, mesAnt }) {
  const [nivel, setNivel] = useState("classe");
  const [dados, setDados] = useState(null);
  const [aberto, setAberto] = useState(null);
  const [sub, setSub] = useState(null);
  useEffect(() => {
    let vivo = true; setDados(null); setAberto(null);
    api.get("/resultado-plano/pbm/itens", { params: { mes, mes_ant: mesAnt || undefined, nivel, limite: 20 } })
      .then((r) => { if (vivo) setDados(r.data); }).catch(() => { if (vivo) setDados({ itens: [], tem_carga_no_mes: false }); });
    return () => { vivo = false; };
  }, [mes, mesAnt, nivel]);
  useEffect(() => {
    if (!aberto) { setSub(null); return; }
    let vivo = true; setSub(null);
    const filtro = nivel === "classe" ? { classe: aberto } : nivel === "principio" ? { principio: aberto } : nivel === "produto" ? { produto: aberto } : null;
    if (!filtro) return;
    const proximo = nivel === "classe" ? "principio" : nivel === "principio" ? "produto" : "beneficiario";
    api.get("/resultado-plano/pbm/itens", { params: { mes, mes_ant: mesAnt || undefined, nivel: proximo, limite: 12, ...filtro } })
      .then((r) => { if (vivo) setSub({ ...r.data, proximo }); }).catch(() => {});
    return () => { vivo = false; };
  }, [aberto, nivel, mes, mesAnt]);
  const seg = (a) => ({ fontSize: 11, padding: "3px 8px", border: "none", cursor: "pointer", background: a ? T.teal : "#fff", color: a ? "#fff" : T.ink, fontWeight: a ? 700 : 400 });
  const num = { ...TD, textAlign: "right", fontFamily: MONO, fontSize: 12, whiteSpace: "nowrap" };
  const rot = (it) => (it.chave === "?" ? "Não identificado" : it.descricao || it.chave);
  if (dados && !dados.tem_carga_no_mes) {
    return <p style={{ fontSize: 12, color: T.inkSoft, margin: "6px 0" }}>Sem arquivo do cartão farmácia importado para {mesLabel(mes)}. Importe o relatório detalhado de compras na aba Cargas para ver os remédios.</p>;
  }
  return (
    <div style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: 8, marginBottom: 8, background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <b style={{ fontSize: 12 }}>Remédios comprados pelo cartão farmácia</b><InfoTip w={360} texto={TIP_PBM_DIAG} />
        <div style={{ display: "inline-flex", border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden" }}>
          {Object.entries(NIVEIS_PBM).map(([k, r]) => <button key={k} onClick={() => setNivel(k)} style={seg(nivel === k)}>{r}</button>)}
        </div>
      </div>
      {!dados ? <p style={{ fontSize: 12, color: T.inkSoft, margin: 0 }}>Carregando…</p> : (
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>
            <th style={{ ...celStatic, textAlign: "left" }}>{NIVEIS_PBM[nivel].replace("Por ", "")}</th>
            {mesAnt && <th style={{ ...celStatic, textAlign: "right" }}>{mesLabel(mesAnt)}</th>}
            <th style={{ ...celStatic, textAlign: "right" }}>{mesLabel(mes)}</th>
            {mesAnt && <th style={{ ...celStatic, textAlign: "right" }}>Variação</th>}
            <th style={{ ...celStatic, textAlign: "right" }} title="pessoas com compra no mês">Pessoas</th>
          </tr></thead>
          <tbody>
            {dados.itens.map((it) => (
              <React.Fragment key={it.chave}>
                <tr onClick={() => setAberto(aberto === it.chave ? null : it.chave)} style={{ cursor: ["classe", "principio", "produto"].includes(nivel) ? "pointer" : "default", background: aberto === it.chave ? T.tealSoft : undefined }}>
                  <td style={{ ...TD, fontSize: 12, fontStyle: it.chave === "?" ? "italic" : undefined }}>{rot(it)}</td>
                  {mesAnt && <td style={num}>{fmt(it.vl_ant)}</td>}
                  <td style={num}>{fmt(it.vl_atual)}</td>
                  {mesAnt && <td style={{ ...num, fontWeight: 600, color: it.delta > 0 ? T.risco : it.delta < 0 ? T.verde : T.inkSoft }}>{it.delta > 0 ? "+" : ""}{fmt(it.delta)}</td>}
                  <td style={num}>{it.pessoas}</td>
                </tr>
                {aberto === it.chave && sub && (
                  <tr><td colSpan={mesAnt ? 5 : 3} style={{ padding: "0 0 6px 18px" }}>
                    <div style={{ borderLeft: `2px solid ${T.teal}`, paddingLeft: 8, fontSize: 12 }}>
                      {sub.itens.map((x) => (
                        <div key={x.chave} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                          <span>{rot(x)}</span>
                          <span style={{ fontFamily: MONO }}>{mesAnt ? `${fmt(x.vl_ant)} → ` : ""}{fmt(x.vl_atual)}</span>
                        </div>
                      ))}
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            <tr><td colSpan={mesAnt ? 5 : 3} style={{ fontSize: 11, color: T.inkSoft, padding: "3px 6px" }}>
              {dados.qt_itens} no total · {mesAnt ? `${fmt(dados.total_ant)} → ` : ""}{fmt(dados.total_atual)}
            </td></tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

function ComposicaoVariacao({ comp, delta, mes, mesAnt, abandono }) {
  const [verAbandono, setVerAbandono] = useState(false);
  if (!comp || !comp.length) return null;
  const cor = (v) => (v > 0 ? T.risco : v < 0 ? T.verde : T.inkSoft);
  const card = { border: `1px solid ${T.line}`, borderRadius: 6, padding: "4px 8px", background: "#fff", minWidth: 128 };
  return (
    <div style={{ margin: "4px 0 8px" }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "stretch" }}>
        <div style={{ fontSize: 11, color: T.inkSoft, alignSelf: "center" }}>
          De onde vem a variação ({mesLabel(mesAnt)} → {mesLabel(mes)}):<InfoTip w={340} texto={TIP_COMPOSICAO} />
        </div>
        {comp.map((c) => (
          <div key={c.efeito} style={card}>
            <div style={{ fontSize: 11, color: T.inkSoft, display: "flex", alignItems: "center" }}>
              {EFEITOS[c.efeito]?.rot || c.efeito} · {c.pessoas} pessoa{c.pessoas === 1 ? "" : "s"}
              {EFEITOS[c.efeito] && <InfoTip w={280} texto={EFEITOS[c.efeito].dica} />}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: cor(c.delta) }}>{c.delta > 0 ? "+" : ""}{fmt(c.delta)}</div>
          </div>
        ))}
        <div style={{ ...card, background: T.tealSoft, minWidth: 90 }}>
          <div style={{ fontSize: 11, color: T.inkSoft }}>Saldo</div>
          <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: cor(delta) }}>{delta > 0 ? "+" : ""}{fmt(delta)}</div>
        </div>
      </div>
      {abandono && abandono.pessoas > 0 && (
        <div style={{ marginTop: 6, border: "1px solid #f3d27a", background: "#fff8e1", borderRadius: 6, padding: "6px 10px", fontSize: 12 }}>
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            <b>Possível abandono:</b>&nbsp;{abandono.pessoas} pessoa{abandono.pessoas === 1 ? "" : "s"} que tinham conta com frequência estão há 60 dias ou mais sem nenhuma
            &nbsp;(somavam {fmt(abandono.gasto_mensal_tipico)} por mês)<InfoTip w={340} texto={TIP_ABANDONO} />
          </span>
          <button onClick={() => setVerAbandono((v) => !v)} style={{ marginLeft: 8, fontSize: 11, border: `1px solid ${T.line}`, background: "#fff", borderRadius: 4, cursor: "pointer", padding: "1px 6px" }}>
            {verAbandono ? "ocultar" : `ver as ${Math.min(abandono.maiores.length, 20)} maiores`}
          </button>
          {verAbandono && (
            <table style={{ borderCollapse: "collapse", width: "100%", marginTop: 6 }}>
              <thead><tr>
                <th style={{ ...TD, fontSize: 11, textAlign: "left", color: T.inkSoft }}>Beneficiário</th>
                <th style={{ ...TD, fontSize: 11, textAlign: "right", color: T.inkSoft }}>Última conta</th>
                <th style={{ ...TD, fontSize: 11, textAlign: "right", color: T.inkSoft }}>Meses com conta (de 6)</th>
                <th style={{ ...TD, fontSize: 11, textAlign: "right", color: T.inkSoft }}>Gasto mensal típico</th>
              </tr></thead>
              <tbody>
                {abandono.maiores.map((a) => (
                  <tr key={a.chave}>
                    <td style={{ ...TD, fontSize: 12 }}><span style={{ fontFamily: MONO, fontSize: 11, color: T.inkSoft }}>{a.chave}</span> {a.descricao || "—"}</td>
                    <td style={{ ...TD, fontSize: 12, textAlign: "right" }}>{mesLabel(a.ultimo_mes)}</td>
                    <td style={{ ...TD, fontSize: 12, textAlign: "right" }}>{a.meses_com_compra}</td>
                    <td style={{ ...TD, fontSize: 12, textAlign: "right", fontFamily: MONO }}>{fmt(a.gasto_mensal_tipico)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function DetalheBruto({ mes, mesAnt, tp, cdGrupo, nivel, chave }) {
  const outro = nivel === "procedimento" ? "prestador" : "procedimento";
  // aberto por classe/princípio: mostra as apresentações (procedimento), quem cobrou e de quem foi
  const abas = NIVEIS_MEDICAMENTO[nivel] ? ["procedimento", "prestador", "beneficiario"] : [outro, "beneficiario"];
  const [por, setPor] = useState(abas[0]);
  const [dados, setDados] = useState(null);
  useEffect(() => {
    let vivo = true;
    setDados(null);
    const params = { mes, mes_ant: mesAnt || undefined, cd_grupo: cdGrupo, por, tp: tp || undefined, limite: 20 };
    if (nivel === "procedimento") params.procedimento = chave;
    else if (nivel === "prestador") params.cd_prestador = chave;
    else params[nivel] = chave;   // classe | principio ('?' = não identificado)
    api.get("/resultado-plano/utilizacao/detalhe", { params })
      .then((r) => { if (vivo) setDados(r.data); }).catch(() => { if (vivo) toast.error("Falha ao detalhar o item"); });
    return () => { vivo = false; };
  }, [mes, mesAnt, tp, cdGrupo, nivel, chave, por]);
  const rot = { procedimento: NIVEIS_MEDICAMENTO[nivel] ? "Apresentações" : "Procedimentos", prestador: "Prestadores", beneficiario: "Beneficiários" };
  const seg = (a) => ({ fontSize: 11, padding: "2px 8px", border: "none", cursor: "pointer", background: a ? T.ink : "#fff", color: a ? "#fff" : T.ink });
  const cel = { ...TD, fontSize: 12, padding: "3px 6px" };
  return (
    <div style={{ borderLeft: `2px solid ${T.teal}`, paddingLeft: 10 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: T.inkSoft }}>{mesAnt ? `${mesLabel(mesAnt)} → ${mesLabel(mes)}` : `Em ${mesLabel(mes)}`}, dentro deste item:</span>
        <div style={{ display: "inline-flex", border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden" }}>
          {abas.map((k) => <button key={k} onClick={() => setPor(k)} style={seg(por === k)}>{rot[k]}</button>)}
        </div>
      </div>
      {dados && dados.mes_ant && <ComposicaoVariacao comp={dados.composicao} delta={dados.delta_total} mes={mes} mesAnt={dados.mes_ant} abandono={dados.abandono} />}
      {!dados ? <p style={{ fontSize: 12, color: T.inkSoft, margin: 0 }}>Carregando…</p>
        : !dados.itens.length ? <p style={{ fontSize: 12, color: T.inkSoft, margin: 0 }}>Nada encontrado.</p>
        : dados.mes_ant ? (
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>
            <th style={{ ...cel, textAlign: "left", color: T.inkSoft, fontWeight: 600 }}>Maiores altas e quedas</th>
            <th style={{ ...cel, textAlign: "right", color: T.inkSoft, fontWeight: 600 }}>{mesLabel(dados.mes_ant)}</th>
            <th style={{ ...cel, textAlign: "right", color: T.inkSoft, fontWeight: 600 }}>{mesLabel(mes)}</th>
            <th style={{ ...cel, textAlign: "right", color: T.inkSoft, fontWeight: 600 }}>Variação</th>
          </tr></thead>
          <tbody>
            {dados.itens.map((d) => (
              <tr key={d.chave}>
                <td style={cel}><span style={{ fontFamily: MONO, fontSize: 11, color: T.inkSoft }}>{d.chave}</span> {d.descricao || "—"}</td>
                <td style={{ ...cel, textAlign: "right", fontFamily: MONO, whiteSpace: "nowrap" }}>{fmt(d.vl_ant)}</td>
                <td style={{ ...cel, textAlign: "right", fontFamily: MONO, whiteSpace: "nowrap" }}>{fmt(d.vl_atual)}</td>
                <td style={{ ...cel, textAlign: "right", fontFamily: MONO, whiteSpace: "nowrap", fontWeight: 600, color: d.delta > 0 ? T.risco : T.verde }}>{d.delta > 0 ? "+" : ""}{fmt(d.delta)}</td>
              </tr>
            ))}
            <tr><td colSpan={4} style={{ fontSize: 11, color: T.inkSoft, padding: "3px 6px" }}>
              mostrando as {dados.itens.length} maiores variações de {dados.qt_total} · {fmt(dados.total_ant)} → {fmt(dados.total)}
            </td></tr>
          </tbody>
        </table>
      ) : (
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <tbody>
            {dados.itens.map((d) => (
              <tr key={d.chave}>
                <td style={cel}><span style={{ fontFamily: MONO, fontSize: 11, color: T.inkSoft }}>{d.chave}</span> {d.descricao || "—"}</td>
                <td style={{ ...cel, textAlign: "right", fontFamily: MONO, whiteSpace: "nowrap" }}>{fmt(d.vl_bruto)}</td>
                <td style={{ ...cel, textAlign: "right", fontFamily: MONO, color: T.inkSoft }} title="contas">{d.qt}</td>
              </tr>
            ))}
            {dados.qt_total > dados.itens.length && (
              <tr><td colSpan={3} style={{ fontSize: 11, color: T.inkSoft, padding: "3px 6px" }}>mostrando {dados.itens.length} de {dados.qt_total} · total {fmt(dados.total)}</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Diagnostico({ alvo }) {
  const [meses, setMeses] = useState(null);   // null = carregando
  const [mes, setMes] = useState(alvo?.mes || "");
  const [tp, setTp] = useState(alvo?.tp || "");   // "" = todos, "P" = pré, "O" = pós
  const [varia, setVaria] = useState(null);
  const [comp, setComp] = useState(null);
  const [grupoAberto, setGrupoAberto] = useState(null);   // cd_grupo explodido na lista
  const [eventosAno, setEventosAno] = useState({});   // {ano: eventos_mes} da linha Eventos do balancete
  const btnMini = { fontSize: 12, padding: "4px 10px", borderRadius: 6, border: `1px solid ${T.line}`, background: "#fff", cursor: "pointer", color: T.ink };
  const kfmt = (v) => { const n = Number(v || 0); return Math.abs(n) >= 1e6 ? (n / 1e6).toFixed(1).replace(".", ",") + "M" : Math.abs(n) >= 1e3 ? Math.round(n / 1e3) + "k" : String(Math.round(n)); };

  useEffect(() => {
    api.get("/resultado-plano/utilizacao/meses")
      .then((r) => {
        const ms = r.data || []; setMeses(ms);
        // vindo do Dashboard, abre no mês clicado; senão no último mês FECHADO (o
        // corrente está em andamento: sem balancete e com as contas pela metade)
        const fechados = ms.filter((m) => m < mesCorrente());
        const padrao = fechados.length ? fechados[fechados.length - 1] : ms[ms.length - 1];
        if (ms.length) setMes((atual) => (atual && ms.includes(atual) ? atual : padrao));
        if (alvo?.mes && ms.length && !ms.includes(alvo.mes)) toast(`Utilização de ${mesLabel(alvo.mes)} ainda não sincronizada; mostrando ${mesLabel(ms[ms.length - 1])}.`);
      })
      .catch(() => { setMeses([]); toast.error("Falha ao carregar os meses da utilização"); });
  }, []);

  const mesAnt = useMemo(() => {
    if (!meses || !mes) return null;
    const i = meses.indexOf(mes);
    return i > 0 ? meses[i - 1] : null;
  }, [meses, mes]);

  // `vivo` descarta resposta atrasada: trocar de mês/regime rápido não deixa
  // o resultado do filtro anterior "chegar por último" e ficar na tela.
  useEffect(() => {
    if (!mes) return undefined;
    let vivo = true;
    setComp(null);
    api.get("/resultado-plano/utilizacao/grupos", { params: { mes, tp: tp || undefined, limite: 12 } })
      .then((r) => { if (vivo) setComp(r.data); }).catch(() => { if (vivo) toast.error("Falha ao carregar a composição"); });
    return () => { vivo = false; };
  }, [mes, tp]);

  useEffect(() => {
    if (!mes || !mesAnt) { setVaria(null); return undefined; }
    let vivo = true;
    setVaria(null); setGrupoAberto(null);
    api.get("/resultado-plano/utilizacao/variacao", { params: { mes, mes_ant: mesAnt, tp: tp || undefined, limite: 8 } })
      .then((r) => { if (vivo) setVaria(r.data); }).catch(() => { if (vivo) toast.error("Falha ao carregar a variação"); });
    return () => { vivo = false; };
  }, [mes, mesAnt, tp]);

  // Linha Eventos do balancete (empresa inteira) dos anos envolvidos — para reconciliar.
  useEffect(() => {
    const anos = [...new Set([mes, mesAnt].filter(Boolean).map((m) => m.slice(0, 4)))];
    const faltam = anos.filter((a) => !(a in eventosAno));
    if (!faltam.length) return;
    setEventosAno((e) => ({ ...e, ...Object.fromEntries(faltam.map((a) => [a, undefined])) }));   // pendente
    faltam.forEach((a) => {
      api.get("/resultado-plano/atividades", { params: { ano: a } })
        .then((r) => setEventosAno((e) => ({ ...e, [a]: r.data?.eventos_mes || {} })))
        .catch(() => setEventosAno((e) => ({ ...e, [a]: null })));
    });
  }, [mes, mesAnt, eventosAno]);
  // undefined = ainda carregando; null = não há balancete desse mês (em andamento
  // ou não fechado) ou a consulta falhou; número = valor da linha Eventos.
  const evBalancete = (m) => {
    if (!m) return null;
    const e = eventosAno[m.slice(0, 4)];
    if (e === undefined) return undefined;
    const v = e?.[tp || "todos"]?.[m];
    return v === undefined ? null : v;
  };

  if (meses === null) return <Card><p style={{ color: T.inkSoft }}>Carregando…</p></Card>;
  if (!meses.length) return (
    <Card>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <AlertTriangle size={18} color={T.ambar} />
        <div>
          <p style={{ margin: 0, color: T.ink, fontWeight: 600 }}>Utilização ainda não sincronizada.</p>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: T.inkSoft }}>
            O diagnóstico cruza o financeiro com as contas médicas por grupo de procedimento. Os dados entram na rotina noturna.
          </p>
        </div>
      </div>
    </Card>
  );

  const btnTp = (v, rot) => (
    <button key={v} onClick={() => setTp(v)}
      style={{ ...btnMini, ...(tp === v ? { borderColor: T.teal, color: T.teal, fontWeight: 700, background: T.tealSoft } : {}) }}>{rot}</button>
  );
  const alta = (comp?.grupos || [])[0];

  return (
    <>
      <Card>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13, color: T.inkSoft }}>Competência:</span>
            <select value={mes} onChange={(e) => setMes(e.target.value)} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${T.line}` }}>
              {meses.map((m) => <option key={m} value={m}>{mesLabel(m)}{m >= mesCorrente() ? " (em andamento)" : ""}</option>)}
            </select>
            <InfoTip w={330} texto="Utilização por grupo de procedimento, por COMPETÊNCIA (data de apresentação, que é a que acompanha o balancete). Valores BRUTOS (antes de glosa/coparticipação/recuperação), como a linha Eventos do balancete." />
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: T.inkSoft }}>Regime:</span>
            {btnTp("", "Todos")}{btnTp("P", "Pré")}{btnTp("O", "Pós")}
          </div>
        </div>
      </Card>

      <Card style={{ marginTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: T.ink }}>O que fez a conta variar: {mesLabel(mes)} {mesAnt ? <>vs {mesLabel(mesAnt)}</> : ""}</h3>
          <InfoTip w={320} texto="Variação do gasto bruto de cada grupo de procedimento em relação à competência anterior. Vermelho subiu (puxou a conta), verde caiu. É a resposta para 'o que puxou a conta'." />
        </div>
        {!mesAnt ? <p style={{ color: T.inkSoft, fontSize: 13 }}>Sem competência anterior para comparar.</p>
          : !varia ? <p style={{ color: T.inkSoft }}>Carregando…</p>
          : !varia.grupos.length ? <p style={{ color: T.inkSoft }}>Sem dados de variação.</p> : (
          <>
            <div style={{ fontSize: 13, color: T.inkSoft, marginBottom: 8 }}>
              Variação total: <b style={{ color: varia.delta_total >= 0 ? T.risco : T.verde }}>{varia.delta_total >= 0 ? "+" : ""}{fmt(varia.delta_total)}</b>
              {" "}({fmt(varia.total_ant)} → {fmt(varia.total_atual)})
            </div>
            <Reconciliacao
              balAnt={evBalancete(mesAnt)} balAtual={evBalancete(mes)}
              utiAnt={varia.total_ant_eventos ?? varia.total_ant} utiAtual={varia.total_atual_eventos ?? varia.total_atual}
              fora={varia.fora_eventos}
              regime={tp === "P" ? "Pré" : tp === "O" ? "Pós" : "Pré + Pós"} />

            {/* 40px por grupo e rótulo encurtado: nome longo quebrava em 3 linhas e
                sobrepunha o vizinho (achado do QA no navegador). O nome inteiro fica no tooltip. */}
            <ResponsiveContainer width="100%" height={Math.max(180, varia.grupos.length * 40)}>
              <ComposedChart data={varia.grupos} layout="vertical" margin={{ top: 4, right: 70, left: 10, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.line} horizontal={false} />
                <XAxis type="number" tickFormatter={kfmt} tick={{ fontSize: 11, fill: T.inkSoft }} />
                <YAxis type="category" dataKey="ds_grupo" width={200} interval={0}
                  tick={{ fontSize: 11, fill: T.ink }}
                  tickFormatter={(v) => (String(v).length > 32 ? String(v).slice(0, 30) + "…" : v)} />
                <RTooltip formatter={(v) => fmt(v)} labelFormatter={(l) => l} />
                <Bar dataKey="delta" radius={[0, 4, 4, 0]} cursor="pointer"
                  onClick={(d) => { const cd = d?.payload?.cd_grupo ?? d?.cd_grupo; if (cd !== undefined && cd !== null) setGrupoAberto((g) => (g === cd ? null : cd)); }}>
                  {varia.grupos.map((g, i) => <Cell key={i} fill={g.delta >= 0 ? T.risco : T.verde} />)}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
            <p style={{ fontSize: 11, color: T.inkSoft, marginTop: 4 }}>Vermelho = subiu (puxou a conta) · Verde = caiu. <b>Clique num grupo (na barra ou na lista) para ver o que está dentro.</b></p>
            <div style={{ marginTop: 6 }}>
              {varia.grupos.map((g) => {
                const aberto = grupoAberto === g.cd_grupo;
                return (
                  <div key={g.cd_grupo}>
                    <div onClick={() => setGrupoAberto(aberto ? null : g.cd_grupo)}
                      style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 6px", cursor: "pointer", borderTop: `1px solid ${T.line}`, background: aberto ? T.tealSoft : undefined, fontSize: 13 }}>
                      {aberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <span style={{ flex: 1, color: T.ink }}>{g.ds_grupo}</span>
                      <span style={{ fontFamily: MONO, fontSize: 12, color: T.inkSoft, whiteSpace: "nowrap" }}>{fmt(g.vl_ant)} → {fmt(g.vl_atual)}</span>
                      <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, width: 110, textAlign: "right", color: g.delta >= 0 ? T.risco : T.verde }}>{g.delta >= 0 ? "+" : ""}{fmt(g.delta)}</span>
                    </div>
                    {aberto && <ExplodirGrupo mes={mes} mesAnt={mesAnt} tp={tp} grupo={g} />}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>

      <Card style={{ marginTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: T.ink }}>Composição de {mesLabel(mes)}</h3>
          <InfoTip w={320} texto="Grupos de procedimento ranqueados por valor bruto na competência (grão maior primeiro; a cauda vira 'Outros'). Bruto de utilização. Para auditar, compare o total com a linha Eventos do Acumulado." />
        </div>
        {!comp ? <p style={{ color: T.inkSoft }}>Carregando…</p>
          : !comp.grupos.length ? <p style={{ color: T.inkSoft }}>Sem dados nesta competência.</p> : (
          <>
            <div style={{ fontSize: 13, color: T.inkSoft, marginBottom: 8 }}>Total (bruto): <b style={{ color: T.ink }}>{fmt(comp.total)}</b></div>
            <div>
              {comp.grupos.map((g) => {
                const pct = comp.total ? Math.max(2, Math.round(g.vl_bruto / comp.total * 100)) : 0;
                return (
                  <div key={g.cd_grupo ?? g.ds_grupo} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0" }}>
                    <div style={{ width: 200, fontSize: 12, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={g.ds_grupo}>{g.ds_grupo}</div>
                    <div style={{ flex: 1, background: T.tealSoft, borderRadius: 4, height: 16 }}>
                      <div style={{ width: `${pct}%`, background: T.teal, height: 16, borderRadius: 4 }} />
                    </div>
                    <div style={{ width: 110, textAlign: "right", fontFamily: MONO, fontSize: 12, color: T.ink }}>{fmt(g.vl_bruto)}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        <p style={{ fontSize: 11, color: T.inkSoft, marginTop: 10, borderTop: `1px solid ${T.line}`, paddingTop: 8 }}>
          Base: contas médicas por competência (data de apresentação), valores brutos, no plano do atendimento. Auditoria: a soma dos grupos deve bater com o movimento da linha <b>Eventos</b> no Acumulado (diferença = escopo a afinar com a contabilidade).
        </p>
      </Card>
    </>
  );
}

/* ---------- Dashboard: evolução mês a mês em gráficos (#122) ---------- */
function Dashboard({ ano, incluirSemPlano, onDiagnosticar }) {
  const [dados, setDados] = useState(null);
  useEffect(() => {
    setDados(null);
    api.get("/resultado-plano/atividades", { params: { ano, incluir_sem_plano: incluirSemPlano } })
      .then((r) => setDados(r.data)).catch(() => toast.error("Falha ao carregar o dashboard"));
  }, [ano, incluirSemPlano]);
  if (!dados) return <Card><p style={{ color: T.inkSoft }}>Carregando…</p></Card>;
  const meses = dados.meses || [];
  if (!meses.length) return <Card><p style={{ color: T.inkSoft }}>Sem dados para {ano}.</p></Card>;
  const por = {}; (dados.atividades || []).forEach((a) => { por[a.chave] = a; });
  const kfmt = (v) => {
    const n = Number(v || 0);
    if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1).replace(".", ",") + "M";
    if (Math.abs(n) >= 1e3) return Math.round(n / 1e3) + "k";
    return String(Math.round(n));
  };
  const serieResultado = meses.map((m) => ({
    mes: mesLabel(m), iso: m,
    Operacional: por.operacional?.resultado_mes?.[m] ?? 0,
    Administrativa: por.administrativa?.resultado_mes?.[m] ?? 0,
    Financeira: por.financeira?.resultado_mes?.[m] ?? 0,
    Total: por.total?.resultado_mes?.[m] ?? 0,
  }));
  const serieOper = meses.map((m) => ({
    mes: mesLabel(m), iso: m,
    Receita: por.operacional?.receita_mes?.[m] ?? 0,
    Despesa: por.operacional?.despesa_mes?.[m] ?? 0,
    Resultado: por.operacional?.resultado_mes?.[m] ?? 0,
  }));
  const ev = dados.eventos_mes || {};
  const serieEventos = meses.map((m) => ({ mes: mesLabel(m), iso: m, "Pré": ev.P?.[m] ?? 0, "Pós": ev.O?.[m] ?? 0 }));
  // Clique num mês de qualquer gráfico abre o Diagnóstico daquele mês (#129).
  const clicar = (tp) => (st) => {
    // Índice nulo (clique na legenda/margem) NÃO pode virar 0 = janeiro.
    const bruto = st?.activeTooltipIndex ?? st?.activeIndex;
    const idx = bruto === null || bruto === undefined || bruto === "" ? NaN : Number(bruto);
    const iso = st?.activePayload?.[0]?.payload?.iso
      ?? meses.find((m) => mesLabel(m) === st?.activeLabel)
      ?? (Number.isInteger(idx) ? meses[idx] : undefined);
    if (iso && onDiagnosticar) onDiagnosticar({ mes: iso, tp });
  };
  const dicaClique = <span style={{ fontSize: 11, color: T.inkSoft, marginLeft: "auto" }}>Clique num mês para ver o que fez a conta variar</span>;
  const eixoY = { tickFormatter: kfmt, tick: { fontSize: 12, fill: T.inkSoft }, width: 56, axisLine: false, tickLine: false };
  const eixoX = { dataKey: "mes", tick: { fontSize: 12, fill: T.inkSoft }, axisLine: false, tickLine: false };
  return (
    <>
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: T.ink }}>Resultado mensal por Atividade ({ano})</h3>
          <InfoTip w={300} texto="Resultado (Receita − Despesa) de cada atividade, mês a mês. A linha escura é o Total. Base: empresa inteira, mesmo escopo do Acumulado." />
          {dicaClique}
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={serieResultado} margin={{ top: 8, right: 20, left: 6, bottom: 0 }} onClick={clicar("")} style={{ cursor: "pointer" }}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
            <XAxis {...eixoX} /><YAxis {...eixoY} />
            <RTooltip formatter={(v) => fmt(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="Operacional" stroke={T.teal} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="Administrativa" stroke={T.azul} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="Financeira" stroke={T.ambar} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="Total" stroke={T.ink} strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Card style={{ marginTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: T.ink }}>Operacional: Receita × Despesa por mês ({ano})</h3>
          <InfoTip w={300} texto="Barras de Receita e Despesa operacional por mês; a linha é o Resultado. Ajuda a ver o mês em que a despesa passou a receita." />
          {dicaClique}
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={serieOper} margin={{ top: 8, right: 20, left: 6, bottom: 0 }} onClick={clicar("")} style={{ cursor: "pointer" }}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
            <XAxis {...eixoX} /><YAxis {...eixoY} />
            <RTooltip formatter={(v) => fmt(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Receita" fill={T.verde} radius={[4, 4, 0, 0]} maxBarSize={26} />
            <Bar dataKey="Despesa" fill={T.risco} radius={[4, 4, 0, 0]} maxBarSize={26} />
            <Line type="monotone" dataKey="Resultado" stroke={T.ink} strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      <Card style={{ marginTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: T.ink }}>Gasto assistencial (Eventos) por mês: Pré × Pós ({ano})</h3>
          <InfoTip w={320} texto="Linha Eventos do balancete (bruta, antes de glosa e coparticipação), por regime. É o gasto que o Diagnóstico abre por grupo de procedimento. Clique na barra do Pré ou do Pós para abrir o diagnóstico já filtrado." />
          {dicaClique}
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={serieEventos} margin={{ top: 8, right: 20, left: 6, bottom: 0 }} style={{ cursor: "pointer" }}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
            <XAxis {...eixoX} /><YAxis {...eixoY} />
            <RTooltip formatter={(v) => fmt(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Pré" fill={T.teal} radius={[4, 4, 0, 0]} maxBarSize={26}
              onClick={(d) => { const iso = d?.payload?.iso ?? d?.iso; if (iso && onDiagnosticar) onDiagnosticar({ mes: iso, tp: "P" }); }} />
            <Bar dataKey="Pós" fill={T.azul} radius={[4, 4, 0, 0]} maxBarSize={26}
              onClick={(d) => { const iso = d?.payload?.iso ?? d?.iso; if (iso && onDiagnosticar) onDiagnosticar({ mes: iso, tp: "O" }); }} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
    </>
  );
}

/* ---------- Acumulado: modelo da planilha "Análise Financeira mensal" ---------- */
function Acumulado({ ano }) {
  const [dados, setDados] = useState(null);
  const [ateMes, setAteMes] = useState("");          // "" = até o último mês com dado
  const [mesesDisp, setMesesDisp] = useState([]);
  useEffect(() => { setAteMes(""); setMesesDisp([]); }, [ano]);
  useEffect(() => {
    setDados(null);
    api.get("/resultado-plano/atividades", { params: { ano, ate_mes: ateMes || undefined } })
      .then((r) => { setDados(r.data); if (!ateMes) setMesesDisp(r.data.meses_disponiveis || []); })
      .catch(() => toast.error("Falha ao carregar o acumulado"));
  }, [ano, ateMes]);
  if (!dados) return <Card><p style={{ color: T.inkSoft }}>Carregando…</p></Card>;
  const ats = dados.atividades || [];
  const adm = dados.administrativa || {};
  const cels = dados.operacional_matriz?.celulas || [];
  const thR = { ...TH, textAlign: "right" };
  const periodo = dados.meses.length ? `${mesLabel(dados.meses[0])} a ${mesLabel(dados.meses[dados.meses.length - 1])}` : String(ano);
  const TIP_ATIV = {
    operacional: "Soma das 4 colunas da matriz abaixo (Pré/Pós × Médico/Odonto). Receita = Mensalidades; Despesa = Despesas Operacionais.",
    administrativa: "Receita = Taxa de Administração (pré e pós). Despesa = Despesas Administrativas (contas 4.6).",
    financeira: "Receitas financeiras (contas 3.5) − Despesas financeiras (contas 4.5).",
    total: "Operacional + Administrativa + Financeira, o mesmo TOTAL da planilha.",
    ajustes: "Lançamentos que a planilha não considera em nenhuma atividade: reversões de provisão odontológicas e outras (3.3 não médica), deduções da receita (3.1.1.7 / 3.1.1.9) e despesas não operacionais (4.7). Mostrados à parte para nada sumir.",
    contabil: "Receita e despesa totais do balancete. TOTAL da planilha + Ajustes = Total contábil (a recuperação do pós aparece como receita na planilha e como redutora de despesa no balancete; o resultado é o mesmo).",
  };
  const LINHAS_MATRIZ = [
    ["Mensalidades / Receitas Operacionais", "receita", true, "Pré: contraprestação do participante + reversões de provisão médicas (3.3), como a planilha. Pós: recuperação/reembolso da contratante (no pós-estabelecido os eventos são repassados e reembolsados)."],
    ["Despesas", "despesas", true, "Eventos − Glosa − Coparticipação."],
    ["Eventos", "eventos", false, "Despesas com eventos/sinistro (bruto)."],
    ["( − ) Glosa", "glosa", false, "Glosas do período (redutoras, negativas)."],
    ["( − ) Coparticipação", "coparticipacao", false, "Coparticipação recuperada do beneficiário (redutora)."],
    ["Ressarcimento ao SUS", "sus", false, "Contas 4.1.1.8, separadas pelo regime da própria conta (pré ou pós)."],
    ["Constituição PEONA", "peona", false, "Provisão de eventos ocorridos e não avisados (4.1.4). Como na planilha, fica no Pré Médico."],
    ["Outras Despesas Operacionais", "outras", false, "Contas 4.4. Como na planilha, ficam no Pré Médico."],
    ["Despesas Operacionais", "despesa", true, "Despesas + Ressarcimento SUS + PEONA + Outras."],
    ["Resultado", "resultado", true, "Mensalidades − Despesas Operacionais."],
  ];
  return (
    <>
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: T.ink }}>Análise das Atividades ({periodo})</h3>
          <InfoTip w={340} texto="Mesmo modelo da planilha 'Análise Financeira mensal', calculado direto do balancete da empresa inteira (todos os planos + administrativo). Use 'Acumulado até' para comparar o mesmo período da planilha." />
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: T.inkSoft }}>Acumulado até:</span>
            <select value={ateMes} onChange={(e) => setAteMes(e.target.value)} style={{ padding: "5px 8px", borderRadius: 8, border: `1px solid ${T.line}` }}>
              <option value="">Último mês disponível</option>
              {mesesDisp.map((m) => <option key={m} value={m}>{mesLabel(m)}</option>)}
            </select>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 560 }}>
            <thead><tr>
              <th style={{ ...TH, textAlign: "left" }}>Atividade</th>
              <th style={thR}>Receita</th><th style={thR}>Despesa</th><th style={thR}>Resultado</th>
            </tr></thead>
            <tbody>
              {ats.map((a) => {
                const ehTotal = a.chave === "total";
                const apoio = a.chave === "ajustes" || a.chave === "contabil";
                return (
                  <tr key={a.chave} style={{ background: ehTotal ? "#eef3f8" : "transparent", color: apoio ? T.inkSoft : undefined, borderTop: a.chave === "ajustes" ? `2px solid ${T.line}` : undefined }}>
                    <td style={{ ...TD, fontWeight: ehTotal ? 800 : apoio ? 400 : 600, fontStyle: apoio ? "italic" : "normal" }}>
                      {a.rotulo}<InfoTip w={320} texto={TIP_ATIV[a.chave] || ""} />
                    </td>
                    <td style={NUM}><Valor v={a.receita} negrito={ehTotal} /></td>
                    <td style={NUM}><Valor v={a.despesa} negrito={ehTotal} /></td>
                    <td style={{ ...NUM, fontWeight: ehTotal ? 800 : 600 }}><Valor v={a.resultado} negrito={ehTotal} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {cels.length > 0 && (
        <Card style={{ marginTop: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 15, color: T.ink }}>Análise da Atividade Operacional: Pré / Pós × Médico / Odontológico</h3>
            <InfoTip w={340} texto="Mesmas linhas e regras da planilha. O regime (pré/pós) e o tipo (médico/odonto) vêm da própria conta contábil. Passe o mouse no nome de cada linha para ver a regra." />
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 680 }}>
              <thead><tr>
                <th style={{ ...TH, textAlign: "left", minWidth: 260 }}>Linha</th>
                {cels.map((c) => <th key={c.chave} style={thR}>{c.rotulo}</th>)}
                <th style={thR}>Total</th>
              </tr></thead>
              <tbody>
                {LINHAS_MATRIZ.map(([rot, campo, forte, tip]) => {
                  const destaque = campo === "despesa" || campo === "resultado";
                  const sub = ["eventos", "glosa", "coparticipacao"].includes(campo);
                  const tot = cels.reduce((s, c) => s + (c[campo] || 0), 0);
                  return (
                    <tr key={campo} style={{ background: destaque ? "#eef3f8" : "transparent" }}>
                      <td style={{ ...TD, fontWeight: forte ? 700 : 400, paddingLeft: sub ? 26 : 8 }}>
                        {rot}<InfoTip w={300} texto={tip} />
                      </td>
                      {cels.map((c) => <td key={c.chave} style={NUM}><Valor v={c[campo]} negrito={destaque} /></td>)}
                      <td style={{ ...NUM, fontWeight: 700 }}><Valor v={tot} negrito /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card style={{ marginTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: T.ink }}>Análise da Atividade Administrativa ({periodo})</h3>
          <InfoTip w={310} texto="Taxa de Administração separada por regime: Pré = planos de pré-pagamento; Pós = pós-estabelecido. Resultado = Taxa de Administração − Despesas Administrativas (4.6)." />
        </div>
        <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: 520 }}>
          <tbody>
            {[["Tx. Adm. Planos PRÉ", adm.taxa_adm_pre],
              ["Tx. Adm. Planos PÓS", adm.taxa_adm_pos],
              ["Receita (Taxa de Administração)", adm.receita],
              ["( − ) Despesas Administrativas", adm.despesa]].map(([r, v]) => (
              <tr key={r}><td style={TD}>{r}</td><td style={NUM}><Valor v={v} /></td></tr>
            ))}
            <tr style={{ background: "#eef3f8" }}>
              <td style={{ ...TD, fontWeight: 800 }}>Resultado</td>
              <td style={{ ...NUM, fontWeight: 800 }}><Valor v={adm.resultado} negrito /></td>
            </tr>
          </tbody>
        </table>
      </Card>
    </>
  );
}

/* ---------- Comparativo por plano ---------- */
function Comparativo({ ano, meses, incluirSemPlano }) {
  const { user } = useAuth();
  const [mes, setMes] = useState("");
  const [dados, setDados] = useState(null);
  const [planosLista, setPlanosLista] = useState([]);
  const [sel, setSel] = useState(null);   // Set de cd_plano; null enquanto carrega a lista
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const boxRef = useRef(null);
  // Chave por usuario+navegador: a escolha do "Por plano" fica salva localmente,
  // separada da selecao GLOBAL de "Regras e planos" (por isso nao reverte mais).
  const chaveSel = `rp_porplano_planos_${user?.id ?? user?.email ?? "anon"}`;

  // Lista de planos (todos). Selecao inicial = a salva no navegador; sem salva,
  // cai no default = os planos do painel.
  useEffect(() => {
    api.get("/resultado-plano/planos")
      .then((r) => {
        const lista = r.data || [];
        setPlanosLista(lista);
        const validos = new Set(lista.map((p) => p.cd_plano));
        let inicial = null;
        try {
          const salvo = JSON.parse(localStorage.getItem(chaveSel) || "null");
          if (Array.isArray(salvo)) inicial = new Set(salvo.filter((cd) => validos.has(cd)));
        } catch { /* localStorage indisponivel: usa default */ }
        if (!inicial) inicial = new Set(lista.filter((p) => p.entra_no_painel).map((p) => p.cd_plano));
        setSel(inicial);
      })
      .catch(() => toast.error("Falha ao carregar os planos"));
  }, [chaveSel]);

  // Persiste a escolha do usuario (nao reverte ao trocar de aba/recarregar).
  useEffect(() => {
    if (sel === null) return;
    try { localStorage.setItem(chaveSel, JSON.stringify([...sel])); } catch { /* ignore */ }
  }, [sel, chaveSel]);

  useEffect(() => {
    if (sel === null) return;                 // espera a seleção inicial
    if (sel.size === 0) { setDados(null); return; }
    setDados(null);
    api.get("/resultado-plano/comparativo", { params: { ano, mes: mes || undefined, incluir_sem_plano: incluirSemPlano, planos: [...sel].join(",") } })
      .then((r) => setDados(r.data)).catch(() => toast.error("Falha ao carregar o comparativo"));
  }, [ano, mes, incluirSemPlano, sel]);

  // Fecha o dropdown ao clicar fora.
  useEffect(() => {
    const fora = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setAberto(false); };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  const toggle = (cd) => setSel((prev) => { const n = new Set(prev); n.has(cd) ? n.delete(cd) : n.add(cd); return n; });
  const btnMini = { fontSize: 12, padding: "3px 8px", borderRadius: 6, border: `1px solid ${T.line}`, background: "#fff", cursor: "pointer", color: T.ink };
  // Pre/Pos vem do TP_PAGAMENTO do plano (DBAPS.PLANO): 'O'=pos (reembolsado),
  // 'P'=pre (custo da operadora). 'S'/outros nao entram no filtro pre/pos.
  const TP = { O: { rot: "Pós", cor: T.teal, soft: T.tealSoft }, P: { rot: "Pré", cor: T.ambar, soft: T.ambarSoft } };
  const planosPorTp = (tp) => planosLista.filter((p) => p.tp_pagamento === tp).map((p) => p.cd_plano);
  const temTp = planosLista.some((p) => p.tp_pagamento === "O" || p.tp_pagamento === "P");
  const nomePlano = (cd) => { const p = planosLista.find((x) => x.cd_plano === cd); return p ? `${p.cd_plano} · ${String(p.ds_plano || "").slice(0, 18)}` : String(cd); };
  const filtrados = planosLista.filter((p) => {
    const q = busca.trim().toLowerCase();
    return !q || String(p.cd_plano).includes(q) || String(p.ds_plano || "").toLowerCase().includes(q);
  });

  // Arvore expansivel (mesma abertura do Painel): comparar planos ate os niveis detalhados.
  const [fechadas, setFechadas] = useState(() => new Set());
  const filhos = useMemo(() => {
    const m = {};
    (dados?.linhas || []).forEach((l) => { if (l.pai) (m[l.pai] = m[l.pai] || []).push(l.codigo); });
    return m;
  }, [dados]);
  const oculta = (l) => { let p = l.pai; while (p) { if (fechadas.has(p)) return true; p = (dados?.linhas || []).find((x) => x.codigo === p)?.pai; } return false; };
  const alternar = (c) => setFechadas((s) => { const n = new Set(s); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const grupo = (l) => !!filhos[l.codigo];

  return (
    <Card>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, color: T.inkSoft }}>Período:</span>
        <select value={mes} onChange={(e) => setMes(e.target.value)} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${T.line}` }}>
          <option value="">Ano inteiro ({ano})</option>
          {meses.map((m) => <option key={m} value={Number(m.split("-")[1])}>{mesLabel(m)}</option>)}
        </select>
        {dados && dados.qt_nao_classificadas > 0 && <span style={{ fontSize: 12, color: T.ambar }}><AlertTriangle size={13} /> {dados.qt_nao_classificadas} conta(s) fora das linhas (ver aba Painel)</span>}
      </div>

      <div ref={boxRef} style={{ position: "relative", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", border: `1px solid ${T.line}`, borderRadius: 8, padding: "6px 8px", minHeight: 40 }}>
          <span style={{ fontSize: 12, color: T.inkSoft, fontWeight: 600, marginRight: 2 }}>Planos:</span>
          {sel && sel.size === 0 && <span style={{ fontSize: 12, color: T.inkSoft }}>nenhum selecionado</span>}
          {sel && [...sel].slice(0, 8).map((cd) => (
            <span key={cd} style={{ fontSize: 12, background: T.tealSoft, color: T.ink, borderRadius: 12, padding: "2px 4px 2px 8px", display: "inline-flex", gap: 4, alignItems: "center" }}>
              {nomePlano(cd)}
              <span onClick={() => toggle(cd)} title="remover" style={{ cursor: "pointer", color: T.inkSoft, fontWeight: 700, padding: "0 3px" }}>×</span>
            </span>
          ))}
          {sel && sel.size > 8 && <span style={{ fontSize: 12, color: T.inkSoft }}>+{sel.size - 8}</span>}
          <button onClick={() => setAberto((v) => !v)} style={{ ...btnMini, marginLeft: "auto" }}>{aberto ? "Fechar" : "Escolher planos ▾"}</button>
        </div>

        {aberto && (
          <div style={{ position: "absolute", zIndex: 40, top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,.12)", padding: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
              <input autoFocus value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar plano…"
                style={{ flex: 1, padding: "6px 8px", borderRadius: 6, border: `1px solid ${T.line}`, fontSize: 13 }} />
              <button onClick={() => setSel(new Set(planosLista.map((p) => p.cd_plano)))} style={btnMini}>Todos</button>
              <button onClick={() => setSel(new Set())} style={btnMini}>Nenhum</button>
            </div>
            {temTp && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, paddingBottom: 8, borderBottom: `1px solid ${T.line}` }}>
                <span style={{ fontSize: 12, color: T.inkSoft }}>Selecionar por tipo:</span>
                <button onClick={() => setSel(new Set(planosPorTp("O")))} style={{ ...btnMini, borderColor: T.teal, color: T.teal }}>Só Pós</button>
                <button onClick={() => setSel(new Set(planosPorTp("P")))} style={{ ...btnMini, borderColor: T.ambar, color: T.ambar }}>Só Pré</button>
                <span style={{ fontSize: 11, color: T.inkSoft, marginLeft: "auto" }}>Pós = reembolsado · Pré = custo da operadora</span>
              </div>
            )}
            <div style={{ maxHeight: 240, overflowY: "auto" }}>
              {filtrados.length === 0 ? <p style={{ fontSize: 12, color: T.inkSoft, padding: 6 }}>Nenhum plano encontrado.</p> :
                filtrados.map((p) => (
                  <label key={p.cd_plano} style={{ display: "flex", gap: 8, alignItems: "center", padding: "5px 4px", cursor: "pointer", fontSize: 13 }}>
                    <input type="checkbox" checked={sel ? sel.has(p.cd_plano) : false} onChange={() => toggle(p.cd_plano)} />
                    <span style={{ color: p.entra_no_painel ? T.ink : T.inkSoft }}>{p.cd_plano} · {String(p.ds_plano || "").slice(0, 34)}</span>
                    {TP[p.tp_pagamento] && <span style={{ fontSize: 10, fontWeight: 600, color: TP[p.tp_pagamento].cor, background: TP[p.tp_pagamento].soft, borderRadius: 4, padding: "0 5px" }}>{TP[p.tp_pagamento].rot}</span>}
                    {!p.entra_no_painel && <span style={{ fontSize: 10, color: T.inkSoft, border: `1px solid ${T.line}`, borderRadius: 4, padding: "0 4px" }}>fora do painel</span>}
                  </label>
                ))}
            </div>
            <div style={{ fontSize: 11, color: T.inkSoft, marginTop: 6, borderTop: `1px solid ${T.line}`, paddingTop: 6 }}>
              {sel ? sel.size : 0} de {planosLista.length} selecionados · sua escolha fica salva neste navegador.
            </div>
          </div>
        )}
      </div>

      {sel && sel.size === 0 ? <p style={{ color: T.inkSoft }}>Selecione ao menos um plano.</p> : !dados ? <p style={{ color: T.inkSoft }}>Carregando…</p> : (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <button onClick={() => setFechadas(new Set())} style={btnMini}>Expandir tudo</button>
            <button onClick={() => setFechadas(new Set(dados.linhas.filter((l) => filhos[l.codigo]).map((l) => l.codigo)))} style={btnMini}>Recolher tudo</button>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead><tr>
                <th style={{ ...TH, textAlign: "left", minWidth: 260 }}>Linha<InfoTip texto="Uma coluna por plano. Cada valor é o líquido da linha no plano, já com o sinal (receita = crédito − débito; despesa = débito − crédito). Abra as linhas (▸) para comparar nos níveis detalhados, igual ao Painel." /></th>
                {dados.colunas.map((c) => <th key={c.cd_plano} style={{ ...TH, textAlign: "right", maxWidth: 140 }} title={c.ds_plano}>{c.cd_plano || "adm"} · {String(c.ds_plano || "").slice(0, 22)}</th>)}
              </tr></thead>
              <tbody>
                {dados.linhas.filter((l) => !oculta(l)).map((l) => (
                  <tr key={l.codigo} style={{ background: l.nivel === 0 ? "#fafbfc" : "transparent" }}>
                    <td style={{ ...TD, paddingLeft: 8 + l.nivel * 18, fontWeight: l.nivel <= 1 ? 700 : 400, whiteSpace: "nowrap" }}>
                      {grupo(l) ? (
                        <span onClick={() => alternar(l.codigo)} style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                          {fechadas.has(l.codigo) ? <ChevronRight size={14} /> : <ChevronDown size={14} />}{l.rotulo}
                        </span>
                      ) : <span style={{ marginLeft: 18 }}>{l.rotulo}</span>}
                    </td>
                    {dados.colunas.map((c) => <td key={c.cd_plano} style={NUM}><Valor v={l.valores[String(c.cd_plano)]} negrito={l.nivel <= 1} /></td>)}
                  </tr>
                ))}
                {dados.totais.map((t) => (
                  <tr key={t.codigo} style={{ background: "#eef3f8" }}><td style={{ ...TD, fontWeight: 800 }}>{t.rotulo}</td>
                    {dados.colunas.map((c) => <td key={c.cd_plano} style={NUM}><Valor v={t.valores[String(c.cd_plano)]} negrito /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

/* ---------- Regras: linhas e planos ---------- */
function Regras({ gestor, aoMudar }) {
  const [linhas, setLinhas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [hist, setHist] = useState([]);
  const [editando, setEditando] = useState(null);   // {codigo, rotulo, regrasTexto, motivo}
  const carregar = useCallback(() => {
    Promise.all([api.get("/resultado-plano/linhas"), api.get("/resultado-plano/planos"), api.get("/resultado-plano/historico", { params: { limite: 50 } })])
      .then(([l, p, h]) => { setLinhas(l.data); setPlanos(p.data); setHist(h.data); })
      .catch(() => toast.error("Falha ao carregar as regras"));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const salvarLinha = async () => {
    const regras = editando.regrasTexto.split("\n").map((s) => s.trim()).filter(Boolean).map((s) => {
      const exata = s.startsWith("=");
      return { tipo: exata ? "exata" : "prefixo", valor: s.replace(/^[=^]\s*/, "") };
    });
    try {
      const body = { rotulo: editando.rotulo, motivo: editando.motivo || undefined };
      if (editando.folha) body.regras = regras;
      await api.put(`/resultado-plano/linhas/${editando.codigo}`, body);
      toast.success("Linha atualizada"); setEditando(null); carregar(); aoMudar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao salvar"); }
  };
  const alternarPlano = async (p) => {
    try { await api.put(`/resultado-plano/planos/${p.cd_plano}`, { entra_no_painel: !p.entra_no_painel }); carregar(); aoMudar(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha ao salvar"); }
  };
  const nivel = (l) => { let n = 0, p = l.pai; while (p) { n++; p = linhas.find((x) => x.codigo === p)?.pai; } return n; };
  const folha = (l) => !linhas.some((x) => x.pai === l.codigo);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)", gap: 16 }}>
      <Card>
        <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>Linhas do painel e regras de classificação</h3>
        <p style={{ fontSize: 12, color: T.inkSoft, margin: "0 0 12px" }}>Cada conta cai na linha cuja regra casa com o código contábil: <code>^</code> prefixo, <code>=</code> conta exata. Em empate vence a regra mais específica. Linhas de grupo somam as filhas.</p>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.codigo} style={{ opacity: l.ativa ? 1 : 0.5 }}>
                <td style={{ ...TD, paddingLeft: 8 + nivel(l) * 16, fontWeight: nivel(l) <= 1 ? 700 : 400, whiteSpace: "nowrap" }}>{l.rotulo}{!l.ativa && " (inativa)"}</td>
                <td style={{ ...TD, fontFamily: MONO, fontSize: 11, color: T.inkSoft }}>
                  {(l.regras || []).map((r, i) => <span key={i} style={{ marginRight: 8 }}>{r.tipo === "exata" ? "=" : "^"}{r.valor}</span>)}
                </td>
                <td style={{ ...TD, textAlign: "right", whiteSpace: "nowrap" }}>
                  {gestor && <Botao pequeno onClick={() => setEditando({ codigo: l.codigo, rotulo: l.rotulo, folha: folha(l), motivo: "",
                    regrasTexto: (l.regras || []).map((r) => (r.tipo === "exata" ? "=" : "^") + r.valor).join("\n") })}>Editar</Botao>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {editando && (
          <div style={{ marginTop: 14, padding: 14, border: `1px solid ${T.teal}`, borderRadius: 10, background: T.tealSoft }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Editar linha <code>{editando.codigo}</code></div>
            <label style={{ fontSize: 12, color: T.inkSoft }}>Rótulo</label>
            <input value={editando.rotulo} onChange={(e) => setEditando({ ...editando, rotulo: e.target.value })} style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${T.line}`, marginBottom: 8 }} />
            {editando.folha && (<>
              <label style={{ fontSize: 12, color: T.inkSoft }}>Regras (uma por linha: <code>^4.1.1.8</code> prefixo, <code>=4.1.1.2.1.1.0.6.1.01</code> exata)</label>
              <textarea rows={5} value={editando.regrasTexto} onChange={(e) => setEditando({ ...editando, regrasTexto: e.target.value })} style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${T.line}`, fontFamily: MONO, fontSize: 12, marginBottom: 8 }} />
            </>)}
            <label style={{ fontSize: 12, color: T.inkSoft }}>Motivo (fica no histórico)</label>
            <input value={editando.motivo} onChange={(e) => setEditando({ ...editando, motivo: e.target.value })} style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${T.line}`, marginBottom: 10 }} />
            <div style={{ display: "flex", gap: 8 }}><Botao primario onClick={salvarLinha}>Salvar</Botao><Botao onClick={() => setEditando(null)}>Cancelar</Botao></div>
          </div>
        )}
      </Card>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card>
          <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>Planos</h3>
          <p style={{ fontSize: 12, color: T.inkSoft, margin: "0 0 10px" }}>Quem entra no painel. Planos novos descobertos no sync chegam desligados até o gestor decidir.</p>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <tbody>
              {planos.map((p) => (
                <tr key={p.cd_plano}>
                  <td style={{ ...TD, whiteSpace: "nowrap" }}>{p.cd_plano} · {p.ds_plano}<div style={{ fontSize: 11, color: T.inkSoft }}>{p.primeiro_mes ? `${mesLabel(p.primeiro_mes.slice(0, 7))} → ${mesLabel(p.ultimo_mes.slice(0, 7))}` : "sem saldo na cópia"}</div></td>
                  <td style={{ ...TD, textAlign: "right" }}>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, cursor: gestor ? "pointer" : "default" }}>
                      <input type="checkbox" checked={!!p.entra_no_painel} disabled={!gestor} onChange={() => alternarPlano(p)} />{p.entra_no_painel ? "no painel" : "fora"}
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card>
          <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>Histórico de alterações</h3>
          {hist.length === 0 ? <p style={{ fontSize: 12, color: T.inkSoft }}>Nenhuma alteração ainda (regras da semente do documento).</p> : hist.map((h) => (
            <div key={h.id} style={{ fontSize: 12, padding: "6px 0", borderBottom: `1px solid ${T.line}` }}>
              <b>{h.entidade} {h.chave}</b> · {h.campo} · {h.alterado_por || "—"} · {dataHora(h.alterado_em)}
              {h.motivo && <div style={{ color: T.inkSoft }}>{h.motivo}</div>}
              <div style={{ fontFamily: MONO, fontSize: 11, color: T.inkSoft }}>{JSON.stringify(h.valor_antes)} → {JSON.stringify(h.valor_depois)}</div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

/* ---------- Cargas ---------- */
const TIP_PBM = "O cartão farmácia (PBM) cobra a operadora num código genérico (ex.: 90000000 MEDICAMENTOS FARMÁCIA), sem dizer qual remédio foi comprado. O relatório detalhado de compras que o administrador do cartão envia traz cada item: remédio, código de barras, quantidade, valor e médico. Importado aqui, ele abre a farmácia por remédio no Diagnóstico. Cada compra é ligada ao beneficiário pelo CPF do titular e pelo nome de quem comprou; o remédio é traduzido para princípio ativo e classe pela base pública da ANVISA. Importar o mesmo período de novo substitui a carga anterior.";

function ImportarPbm({ gestor }) {
  const [cargas, setCargas] = useState([]);
  const [arquivo, setArquivo] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const carregar = () => api.get("/resultado-plano/pbm/cargas").then((r) => setCargas(r.data)).catch(() => {});
  useEffect(() => { carregar(); }, []);
  const enviar = async (rota) => {
    if (!arquivo) return toast.error("Escolha o arquivo");
    const fd = new FormData(); fd.append("arquivo", arquivo);
    setOcupado(true);
    try {
      const r = await api.post(`/resultado-plano/pbm/${rota}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      if (rota === "previa") setPrevia(r.data);
      else {
        toast.success(`Importado: ${r.data.itens} itens de ${r.data.pessoas_ligadas} pessoas${r.data.substituiu ? " (substituiu a carga anterior do mesmo período)" : ""}`);
        setPrevia(null); setArquivo(null); carregar();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao ler o arquivo"); }
    finally { setOcupado(false); }
  };
  const excluir = (c) => {
    if (!window.confirm(`Excluir a carga de ${dataBR(c.periodo_ini)} a ${dataBR(c.periodo_fim)}?`)) return;
    api.delete(`/resultado-plano/pbm/cargas/${c.id}`).then(() => { toast.success("Carga excluída"); carregar(); }).catch(() => toast.error("Erro ao excluir"));
  };
  const pct = (a, b) => (b ? `${Math.round((100 * a) / b)}%` : "—");
  return (
    <Card>
      <h3 style={{ margin: "0 0 4px", fontSize: 15, display: "flex", alignItems: "center" }}>Compras de farmácia pelo cartão (PBM)<InfoTip w={380} texto={TIP_PBM} /></h3>
      <p style={{ fontSize: 12, color: T.inkSoft, margin: "0 0 10px" }}>Relatório detalhado de compras do mês, em Excel. Veja a prévia antes de confirmar.</p>
      {gestor ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
          <input type="file" accept=".xls,.xlsx" onChange={(e) => { setArquivo(e.target.files?.[0] || null); setPrevia(null); }} />
          <button className="btn btn-secondary" disabled={!arquivo || ocupado} onClick={() => enviar("previa")}>{ocupado && !previa ? "Lendo…" : "Pré-visualizar"}</button>
          {previa && <button className="btn btn-primary" disabled={ocupado} onClick={() => enviar("importar")}>{ocupado ? "Importando…" : "Confirmar importação"}</button>}
        </div>
      ) : <p style={{ fontSize: 12, color: T.inkSoft }}>Importar exige o nível gestor do módulo.</p>}
      {previa && (
        <div style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: 10, marginBottom: 12, background: "#fafbfc", fontSize: 13 }}>
          <div><b>Período:</b> {dataBR(previa.periodo_ini)} a {dataBR(previa.periodo_fim)} · <b>{previa.itens}</b> itens em <b>{previa.compras}</b> compras · <b>{fmt(previa.valor_total)}</b></div>
          <div style={{ display: "flex", alignItems: "center" }}><b>Pessoas ligadas ao cadastro:</b>&nbsp;{previa.pessoas_ligadas} de {previa.pessoas} ({pct(previa.pessoas_ligadas, previa.pessoas)}) · {fmt(previa.valor_ligado)} do valor
            <InfoTip w={320} texto="O arquivo traz o CPF do titular e o nome (abreviado) de quem comprou. O sistema acha a família pelo CPF do titular e a pessoa pelo primeiro nome, último nome e iniciais. Quem não liga entra mesmo assim, mas fica fora das visões por beneficiário." /></div>
          <div style={{ display: "flex", alignItems: "center" }}><b>Remédio identificado (princípio ativo):</b>&nbsp;{previa.pct_valor_com_principio}% do valor
            <InfoTip w={300} texto="Parte do valor cujo nome comercial foi traduzido para princípio ativo pela base da ANVISA. O resto é, em geral, o que não é medicamento registrado (dermocosmético, suplemento)." /></div>
          {previa.conflito && (
            <div style={{ color: previa.conflito.mesmo_periodo ? T.inkSoft : T.risco, marginTop: 4 }}>
              {previa.conflito.mesmo_periodo ? "Já existe carga deste mesmo período: ao confirmar, ela será substituída."
                : `Já existe carga de ${dataBR(previa.conflito.periodo_ini)} a ${dataBR(previa.conflito.periodo_fim)} cobrindo parte deste período: exclua-a antes.`}
            </div>
          )}
          {previa.nao_ligadas.length > 0 && (
            <details style={{ marginTop: 4 }}><summary style={{ cursor: "pointer", color: T.inkSoft }}>{previa.pessoas - previa.pessoas_ligadas} pessoa(s) não ligada(s)</summary>
              <ul style={{ margin: "4px 0 0 16px" }}>{previa.nao_ligadas.map((n, i) => <li key={i}>{n.nome} · titular {n.cpf_titular}</li>)}</ul></details>
          )}
        </div>
      )}
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead><tr><th style={{ ...TH, textAlign: "left" }}>Período</th><th style={{ ...TH, textAlign: "left" }}>Importado</th><th style={{ ...TH, textAlign: "right" }}>Itens</th><th style={{ ...TH, textAlign: "right" }}>Pessoas ligadas</th><th style={{ ...TH, textAlign: "right" }}>Valor</th>{gestor && <th style={TH} />}</tr></thead>
        <tbody>
          {cargas.map((c) => (
            <tr key={c.id}>
              <td style={TD}>{dataBR(c.periodo_ini)} a {dataBR(c.periodo_fim)}</td>
              <td style={{ ...TD, fontSize: 12 }}>{dataHora(c.importado_em)}{c.importado_por ? ` · ${c.importado_por}` : ""} · {c.origem}</td>
              <td style={NUM}>{c.itens}</td><td style={NUM}>{c.pessoas_ligadas} de {c.pessoas}</td><td style={NUM}>{fmt(c.valor_total)}</td>
              {gestor && <td style={TD}><button className="btn btn-secondary" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => excluir(c)}>Excluir</button></td>}
            </tr>
          ))}
          {cargas.length === 0 && <tr><td colSpan={6} style={{ ...TD, color: T.inkSoft }}>Nenhum arquivo importado ainda.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function Cargas() {
  const [cargas, setCargas] = useState([]);
  useEffect(() => { api.get("/resultado-plano/cargas").then((r) => setCargas(r.data)).catch(() => {}); }, []);
  return (
    <Card>
      <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>Cargas da cópia local</h3>
      <p style={{ fontSize: 12, color: T.inkSoft, margin: "0 0 12px" }}>Cada sync compara, mês a mês, contagem e somas da view do Oracle com a cópia. Divergência marca a carga como falha.</p>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead><tr><th style={{ ...TH, textAlign: "left" }}>Quando</th><th style={{ ...TH, textAlign: "left" }}>Origem</th><th style={{ ...TH, textAlign: "left" }}>Janela</th><th style={{ ...TH, textAlign: "right" }}>Lidas</th><th style={{ ...TH, textAlign: "right" }}>Gravadas</th><th style={{ ...TH, textAlign: "right" }}>Removidas</th><th style={TH}>Resultado<InfoTip texto="A carga compara a view do Oracle × a cópia local por mês (reconciliação). Divergência marca a carga como não-ok: o dado fica, mas sobe alerta." /></th></tr></thead>
        <tbody>
          {cargas.map((c) => (
            <tr key={c.id}>
              <td style={TD}>{dataHora(c.dt_execucao)}</td><td style={TD}>{c.origem}{c.executado_por ? ` · ${c.executado_por}` : ""}</td>
              <td style={TD}>{c.mes_ini ? `${mesLabel(c.mes_ini.slice(0, 7))} → ${mesLabel(c.mes_fim.slice(0, 7))}` : "—"}</td>
              <td style={NUM}>{c.qt_lidas}</td><td style={NUM}>{c.qt_gravadas}</td><td style={NUM}>{c.qt_removidas}</td>
              <td style={{ ...TD, color: c.ok ? T.verde : T.risco, fontSize: 12 }}>{c.ok ? "ok" : (c.erro || "falha")}{c.reconciliacao?.divergentes > 0 && ` · ${c.reconciliacao.divergentes} mês(es) divergente(s)`}</td>
            </tr>
          ))}
          {cargas.length === 0 && <tr><td colSpan={7} style={{ ...TD, color: T.inkSoft }}>Nenhuma carga ainda.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

/* ---------- Pagina ---------- */
export default function ResultadoPlano() {
  const { temNivel } = useAuth();
  const gestor = temNivel("resultado_plano", "gestor");
  const [aba, setAba] = useState("painel");
  const [diagAlvo, setDiagAlvo] = useState(null);   // {mes, tp} vindo do clique no Dashboard
  const [status, setStatus] = useState(null);
  const [meses, setMeses] = useState([]);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [incluirSemPlano, setIncluirSemPlano] = useState(false);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [drill, setDrill] = useState(null);
  const [versao, setVersao] = useState(0);
  const [tend, setTend] = useState(null);

  const anos = useMemo(() => Array.from(new Set(meses.map((m) => Number(m.slice(0, 4))))).sort((a, b) => b - a), [meses]);
  const mesesDoAno = useMemo(() => meses.filter((m) => m.startsWith(String(ano))), [meses, ano]);

  useEffect(() => {
    api.get("/resultado-plano/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/resultado-plano/meses").then((r) => { setMeses(r.data || []); const ultimo = (r.data || []).slice(-1)[0]; if (ultimo) setAno(Number(ultimo.slice(0, 4))); }).catch(() => {});
  }, [versao]);

  useEffect(() => {
    setCarregando(true);
    api.get("/resultado-plano/painel", { params: { ano, incluir_sem_plano: incluirSemPlano } })
      .then((r) => setDados(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Falha ao carregar o painel"))
      .finally(() => setCarregando(false));
    // Tendência é leitura auxiliar: se falhar, o painel continua inteiro.
    api.get("/resultado-plano/tendencias", { params: { ano, incluir_sem_plano: incluirSemPlano } })
      .then((r) => setTend(r.data)).catch(() => setTend(null));
  }, [ano, incluirSemPlano, versao]);

  // (codigo da linha | mes) -> variacao, para marcar a celula na tabela
  const destaques = useMemo(() => {
    const m = new Map();
    (tend?.variacoes || []).forEach((v) => m.set(`${v.codigo}|${v.mes}`, v));
    return m;
  }, [tend]);

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const r = await api.post("/resultado-plano/sincronizar");
      if (r.data.ok) toast.success(`Cópia atualizada: ${r.data.qt_gravadas} saldos gravados`); else toast.error(r.data.erro || "Sync falhou");
      setVersao((v) => v + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao sincronizar"); }
    finally { setSincronizando(false); }
  };
  const fecharDrill = useCallback(() => setDrill(null), []);

  return (
    <div style={{ background: T.bg, minHeight: "100%", padding: "22px 26px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8, color: T.ink }}><PieChart size={22} color={T.teal} /> Resultado por Plano</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: T.inkSoft, maxWidth: 720 }}>DRE mensal por plano a partir da contabilidade do MV. Receita: crédito − débito; despesa: débito − crédito. Setores sem plano (administrativos) ficam fora, salvo quando marcado.</p>
        </div>
        <div style={{ textAlign: "right", fontSize: 12, color: T.inkSoft }}>
          {status && (<>
            <div>{status.fonte_disponivel ? <span style={{ color: T.verde }}>fonte do Oracle instalada</span> : <span style={{ color: T.ambar }}><AlertTriangle size={12} /> fonte do Oracle não instalada nesta instância</span>}</div>
            <div>cópia: {status.qt_saldos.toLocaleString("pt-BR")} saldos{status.primeiro_mes && ` · ${mesLabel(status.primeiro_mes.slice(0, 7))} → ${mesLabel(status.ultimo_mes.slice(0, 7))}`}</div>
            <div>última carga: {status.ultima_carga ? `${dataHora(status.ultima_carga.dt_execucao)} · ${status.ultima_carga.ok ? "ok" : (status.ultima_carga.erro || "falha")}` : "nunca"}</div>
          </>)}
          {gestor && <div style={{ marginTop: 6 }}><Botao primario onClick={sincronizar} disabled={sincronizando}><RefreshCw size={14} className={sincronizando ? "spin" : ""} />{sincronizando ? "Atualizando…" : "Atualizar agora"}</Botao></div>}
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${T.line}`, marginBottom: 16, flexWrap: "wrap" }}>
        <Aba ativa={aba === "painel"} onClick={() => setAba("painel")} icone={PieChart}>Painel</Aba>
        <Aba ativa={aba === "acumulado"} onClick={() => setAba("acumulado")} icone={LayoutGrid}>Acumulado</Aba>
        <Aba ativa={aba === "planos"} onClick={() => setAba("planos")} icone={Layers}>Por plano</Aba>
        <Aba ativa={aba === "analitico"} onClick={() => setAba("analitico")} icone={TrendingUp}>Analítico</Aba>
        <Aba ativa={aba === "dashboard"} onClick={() => setAba("dashboard")} icone={BarChart3}>Dashboard</Aba>
        <Aba ativa={aba === "diagnostico"} onClick={() => setAba("diagnostico")} icone={Activity}>Diagnóstico</Aba>
        <Aba ativa={aba === "regras"} onClick={() => setAba("regras")} icone={Settings}>Regras e planos</Aba>
        <Aba ativa={aba === "cargas"} onClick={() => setAba("cargas")} icone={Database}>Cargas</Aba>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center", paddingBottom: 6 }}>
          <select value={ano} onChange={(e) => setAno(Number(e.target.value))} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${T.line}` }}>
            {(anos.length ? anos : [ano]).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <label style={{ fontSize: 12, color: T.inkSoft, display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={incluirSemPlano} onChange={(e) => setIncluirSemPlano(e.target.checked)} /> incluir administrativo (não rateado)
            <InfoTip w={340} texto="Cada lançamento entra no plano pelo setor (centro de custo) em que foi feito. Setores que não são de nenhum plano, como RH, TI e diretoria, são o administrativo: o ERP não divide esse custo entre os planos. Desmarcado, o painel mostra o resultado dos planos sem esse custo, e ele aparece à parte em 'Setor sem plano'. Marcado, ele é somado às linhas e o total passa a fechar com o da contabilidade." />
          </label>
        </div>
      </div>

      {aba === "painel" && (
        <>
          <Conferencia ano={ano} incluirSemPlano={incluirSemPlano} versao={versao}
            onVerNaoClassificadas={() => setDrill({ codigo: "__nao_classificadas__", rotulo: "Não classificadas", ano, mes: null })} />
          <Anomalias key={`anom-${versao}`} ano={ano} />
          <Tendencias dados={tend} />
          {dados && dados.nao_classificadas.length > 0 && (
            <div style={{ background: T.ambarSoft, border: `1px solid ${T.ambar}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
              {/* O VALOR vem primeiro: três contas fora das regras podem ser R$ 2,00
                  ou R$ 2 milhões, e só a contagem não diz se dá pra confiar no painel. */}
              <AlertTriangle size={14} color={T.ambar} /> <b>{fmt(dados.nao_classificadas_resumo.total)}</b> em{" "}
              <b>{dados.nao_classificadas_resumo.qt_contas}</b> conta(s) não cai em nenhuma linha e fica fora dos totais:
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {dados.nao_classificadas.slice(0, 12).map((f, i) => (
                  <li key={i} style={{ fontFamily: MONO, fontSize: 12, cursor: "pointer" }} onClick={() => setDrill({ codigo: "__nao_classificadas__", rotulo: "Não classificadas", ano, mes: f.mes })}>
                    {f.cd_contabil} · {f.ds_conta} · {mesLabel(f.mes)} · {fmt(f.vl_movimento)}
                  </li>
                ))}
                {dados.nao_classificadas.length > 12 && <li style={{ fontSize: 12 }}>… e mais {dados.nao_classificadas.length - 12}</li>}
              </ul>
            </div>
          )}
          <Card style={{ padding: 8 }}>
            {carregando || !dados ? <p style={{ color: T.inkSoft, padding: 12 }}>Carregando…</p>
              : dados.meses.length === 0 ? <p style={{ color: T.inkSoft, padding: 12 }}>Sem saldos para {ano} na cópia local{status && !status.fonte_disponivel ? ": a fonte do Oracle ainda não foi instalada nesta instância." : "."}</p>
              : <TabelaPainel dados={dados} destaques={destaques} onCelula={(l, m) => setDrill({ codigo: l.codigo, rotulo: l.rotulo, ano, mes: m })} />}
          </Card>
          {dados && <p style={{ fontSize: 12, color: T.inkSoft, marginTop: 8 }}>Planos no painel: {dados.planos.join(", ") || "nenhum"}. Clique num valor para ver as contas. "Total do ano" soma os meses presentes.</p>}
        </>
      )}
      {aba === "acumulado" && <Acumulado ano={ano} />}
      {aba === "dashboard" && <Dashboard ano={ano} incluirSemPlano={incluirSemPlano} onDiagnosticar={(alvo) => { setDiagAlvo(alvo); setAba("diagnostico"); }} />}
      {aba === "diagnostico" && <Diagnostico alvo={diagAlvo} />}
      {aba === "planos" && <Comparativo ano={ano} meses={mesesDoAno} incluirSemPlano={incluirSemPlano} />}
      {aba === "analitico" && (carregando || !dados ? <p style={{ color: T.inkSoft, padding: 12 }}>Carregando…</p> : <Analitico dados={dados} />)}
      {aba === "regras" && <Regras gestor={gestor} aoMudar={() => setVersao((v) => v + 1)} />}
      {aba === "cargas" && <><Cargas key={versao} /><div style={{ height: 12 }} /><ImportarPbm gestor={gestor} /></>}

      {drill && <ModalContas alvo={drill} meses={mesesDoAno} incluirSemPlano={incluirSemPlano} onFechar={fecharDrill} />}
    </div>
  );
}
